import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, apiError } from "../../lib/api";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  Building2, Network, ShieldCheck, ScrollText, Plus, ChevronRight,
  Users2, FileStack, CheckCircle2, Lock, Scale, History, X, ArrowLeftRight, AlertTriangle, ClipboardCheck, Receipt, Landmark, LayoutDashboard,
} from "lucide-react";
import PostFiling from "./PostFiling";
import CommandCenter from "./CommandCenter";

const TENANT_ACTIONS = ["view", "create", "edit", "upload", "prepare", "review", "approve", "reject", "reopen", "file", "export", "manage_users", "manage_rules", "manage_integrations", "override"];

function sapi(env) {
  const h = { "X-Environment": env };
  return {
    get: (u, cfg = {}) => api.get(u, { ...cfg, headers: { ...(cfg.headers || {}), ...h } }),
    post: (u, b, cfg = {}) => api.post(u, b, { ...cfg, headers: { ...(cfg.headers || {}), ...h } }),
  };
}

const TABS = [
  ["command", "لوحة التحكم", "Command Center", LayoutDashboard],
  ["hierarchy", "التسلسل الهرمي", "Hierarchy", Network],
  ["workspace", "مساحة الفترة", "Period Workspace", FileStack],
  ["compliance", "الامتثال", "Compliance", Scale],
  ["reconciliation", "التسويات", "Reconciliation", ArrowLeftRight],
  ["workpapers", "أوراق العمل", "Working Papers", ClipboardCheck],
  ["returns", "الإقرارات", "VAT Returns", Receipt],
  ["postfiling", "ما بعد التقديم", "Post-Filing", Landmark],
  ["rbac", "الصلاحيات", "Permissions", ShieldCheck],
  ["audit", "سجل التدقيق", "Audit Log", ScrollText],
];

const MGMT_LANDING_ROLES = ["platform_admin", "tax_admin", "tax_manager", "management"];

export default function Structure() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const L = (ar, en) => (lang === "ar" ? ar : en);
  const [env, setEnv] = useState("demo");
  const [available, setAvailable] = useState(["demo", "production"]);
  const [tab, setTab] = useState("hierarchy");
  const landingApplied = useRef(false);

  useEffect(() => {
    if (!user || landingApplied.current) return;
    landingApplied.current = true;
    if (MGMT_LANDING_ROLES.includes(user.role)) setTab("command");
  }, [user]);

  useEffect(() => {
    api.get("/environment").then(({ data }) => { setEnv(data.active); setAvailable(data.available); }).catch(() => {});
  }, []);

  const isClient = ["client_admin", "client_user", "client_approver"].includes(user?.role);
  if (isClient) {
    return <div className="p-6 text-sm text-muted-foreground" data-testid="structure-forbidden">{L("هذه الصفحة للموظفين فقط.", "This page is for staff only.")}</div>;
  }

  return (
    <div className="space-y-6" data-testid="structure-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-[hsl(217_63%_15%)]">{L("العمليات الضريبية", "VAT Operations")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{L("إدارة العملاء والكيانات والمجموعات الضريبية ومساحة عمل الفترة.", "Manage clients, entities, VAT groups and the period workspace.")}</p>
        </div>
        <div className="flex items-center gap-2" data-testid="env-switcher">
          <span className="text-xs text-muted-foreground">{L("البيئة", "Environment")}:</span>
          {available.map((e) => (
            <button key={e} data-testid={`env-${e}`} onClick={() => setEnv(e)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${env === e ? (e === "production" ? "bg-[hsl(354_60%_45%)] text-white border-transparent" : "bg-[hsl(217_63%_15%)] text-white border-transparent") : "bg-white text-foreground/70 border-border hover:bg-secondary"}`}>
              {e === "production" ? L("إنتاج", "Production") : L("عرض", "Demo")}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {TABS.map(([key, ar, en, Icon]) => (
          <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === key ? "border-[hsl(217_63%_15%)] text-[hsl(217_63%_15%)]" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Icon className="h-4 w-4" /> {L(ar, en)}
          </button>
        ))}
      </div>

      {tab === "command" && <CommandCenter env={env} user={user} L={L} />}
      {tab === "hierarchy" && <Hierarchy env={env} L={L} />}
      {tab === "workspace" && <Workspace env={env} L={L} />}
      {tab === "compliance" && <Compliance env={env} user={user} L={L} />}
      {tab === "reconciliation" && <Reconciliation env={env} user={user} L={L} />}
      {tab === "workpapers" && <WorkPapers env={env} user={user} L={L} />}
      {tab === "returns" && <Returns env={env} user={user} L={L} />}
      {tab === "postfiling" && <PostFiling env={env} user={user} L={L} />}
      {tab === "rbac" && <Rbac L={L} />}
      {tab === "audit" && <Audit env={env} L={L} />}
    </div>
  );
}

function Card({ children, className = "", ...rest }) {
  return <div className={`bg-white rounded-xl border border-border p-4 ${className}`} {...rest}>{children}</div>;
}
function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(217_63%_15%)]/20" />
    </label>
  );
}

// ---------- Hierarchy ----------
function Hierarchy({ env, L }) {
  const s = sapi(env);
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState(null);
  const [entities, setEntities] = useState([]);
  const [groups, setGroups] = useState([]);
  const [regs, setRegs] = useState([]);
  const [nc, setNc] = useState({ name: "", name_ar: "", cr_number: "" });
  const [ne, setNe] = useState({ legal_name: "", entity_type: "" });
  const [ng, setNg] = useState({ group_name: "", group_vat_number: "" });

  const loadClients = useCallback(() => { s.get("/structure/clients").then(({ data }) => setClients(data)).catch(() => {}); }, [env]);
  useEffect(() => { loadClients(); setSel(null); }, [env]); // eslint-disable-line

  const loadChildren = (c) => {
    setSel(c);
    s.get(`/structure/entities?client_id=${c.id}`).then(({ data }) => setEntities(data)).catch(() => {});
    s.get(`/structure/groups?client_id=${c.id}`).then(({ data }) => setGroups(data)).catch(() => {});
    s.get(`/structure/registrations?client_id=${c.id}`).then(({ data }) => setRegs(data)).catch(() => {});
  };

  const createClient = () => {
    if (!nc.name) return;
    s.post("/structure/clients", nc).then(({ data }) => { toast.success(L("تم إنشاء العميل", "Client created")); setNc({ name: "", name_ar: "", cr_number: "" }); loadClients(); loadChildren(data); })
      .catch((e) => toast.error(apiError(e)));
  };
  const createEntity = () => {
    if (!ne.legal_name || !sel) return;
    s.post("/structure/entities", { ...ne, client_id: sel.id }).then(() => { toast.success(L("تم إنشاء الكيان", "Entity created")); setNe({ legal_name: "", entity_type: "" }); loadChildren(sel); })
      .catch((e) => toast.error(apiError(e)));
  };
  const createGroup = () => {
    if (!ng.group_name || !sel) return;
    s.post("/structure/groups", { ...ng, client_id: sel.id }).then(() => { toast.success(L("تم إنشاء المجموعة", "Group created")); setNg({ group_name: "", group_vat_number: "" }); loadChildren(sel); })
      .catch((e) => toast.error(apiError(e)));
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <Card>
        <div className="flex items-center gap-2 mb-3"><Building2 className="h-4 w-4 text-[hsl(217_63%_15%)]" /><h3 className="font-semibold text-sm">{L("العملاء", "Clients")} ({clients.length})</h3></div>
        <div className="space-y-2 mb-4">
          <Field testid="client-name" label={L("اسم العميل", "Client name")} value={nc.name} onChange={(v) => setNc({ ...nc, name: v })} />
          <Field testid="client-cr" label={L("السجل التجاري", "CR number")} value={nc.cr_number} onChange={(v) => setNc({ ...nc, cr_number: v })} />
          <button data-testid="create-client-btn" onClick={createClient} className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-[hsl(217_63%_15%)] text-white text-sm py-2 font-medium hover:opacity-90"><Plus className="h-4 w-4" />{L("إضافة عميل", "Add client")}</button>
        </div>
        <div className="space-y-1 max-h-80 overflow-y-auto gc-scroll">
          {clients.map((c) => (
            <button key={c.id} data-testid={`client-row-${c.id}`} onClick={() => loadChildren(c)}
              className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm text-start ${sel?.id === c.id ? "bg-secondary" : "hover:bg-secondary/60"}`}>
              <span className="truncate">{c.name || c.name_ar}</span><ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          ))}
          {!clients.length && <div className="text-xs text-muted-foreground py-6 text-center">{L("لا يوجد عملاء في هذه البيئة", "No clients in this environment")}</div>}
        </div>
      </Card>

      <Card className="lg:col-span-2">
        {!sel ? <div className="text-sm text-muted-foreground py-16 text-center">{L("اختر عميلاً لعرض كياناته ومجموعاته.", "Select a client to view entities and groups.")}</div> : (
          <div className="space-y-5" data-testid="client-detail">
            <h3 className="font-semibold text-[hsl(217_63%_15%)]">{sel.name}</h3>

            <section>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("الكيانات القانونية", "Legal Entities")} ({entities.length})</h4>
              <div className="flex flex-wrap gap-2 mb-2">
                <input data-testid="entity-name" value={ne.legal_name} onChange={(e) => setNe({ ...ne, legal_name: e.target.value })} placeholder={L("الاسم القانوني", "Legal name")} className="flex-1 min-w-40 rounded-lg border border-border px-3 py-1.5 text-sm" />
                <button data-testid="create-entity-btn" onClick={createEntity} className="rounded-lg bg-[hsl(217_63%_15%)] text-white text-sm px-3 py-1.5">{L("إضافة", "Add")}</button>
              </div>
              <div className="space-y-1">{entities.map((e) => <div key={e.id} className="rounded-lg border border-border px-3 py-2 text-sm">{e.legal_name}</div>)}</div>
            </section>

            <section>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("التسجيلات الضريبية", "VAT Registrations")} ({regs.length})</h4>
              <RegForm env={env} entities={entities} onDone={() => loadChildren(sel)} L={L} />
              <div className="space-y-1 mt-2">{regs.map((r) => <div key={r.id} className="rounded-lg border border-border px-3 py-2 text-sm flex justify-between"><span>{r.vat_number || "—"}</span><span className="text-muted-foreground text-xs">{r.filing_frequency}</span></div>)}</div>
            </section>

            <section>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("المجموعات الضريبية", "VAT Groups")} ({groups.length})</h4>
              <div className="flex flex-wrap gap-2 mb-2">
                <input data-testid="group-name" value={ng.group_name} onChange={(e) => setNg({ ...ng, group_name: e.target.value })} placeholder={L("اسم المجموعة", "Group name")} className="flex-1 min-w-40 rounded-lg border border-border px-3 py-1.5 text-sm" />
                <button data-testid="create-group-btn" onClick={createGroup} className="rounded-lg bg-[hsl(217_63%_15%)] text-white text-sm px-3 py-1.5">{L("إضافة", "Add")}</button>
              </div>
              {groups.map((g) => <GroupCard key={g.id} env={env} group={g} entities={entities} L={L} />)}
            </section>
          </div>
        )}
      </Card>
    </div>
  );
}

function RegForm({ env, entities, onDone, L }) {
  const s = sapi(env);
  const [eid, setEid] = useState("");
  const [vat, setVat] = useState("");
  const create = () => {
    if (!eid) { toast.error(L("اختر كياناً", "Select an entity")); return; }
    if (!/^\d{15}$/.test(vat)) { toast.error(L("الرقم الضريبي يجب أن يكون 15 رقماً بالضبط", "VAT number must be exactly 15 digits")); return; }
    s.post("/structure/registrations", { holder_type: "entity", legal_entity_id: eid, vat_number: vat, filing_frequency: "monthly" })
      .then(() => { toast.success(L("تم التسجيل", "Registration created")); setVat(""); onDone(); }).catch((e) => toast.error(apiError(e)));
  };
  return (
    <div>
    <div className="flex flex-wrap gap-2">
      <select data-testid="reg-entity" value={eid} onChange={(e) => setEid(e.target.value)} className="rounded-lg border border-border px-2 py-1.5 text-sm">
        <option value="">{L("الكيان", "Entity")}</option>
        {entities.map((e) => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
      </select>
      <input data-testid="reg-vat" value={vat} onChange={(e) => setVat(e.target.value.replace(/\D/g, "").slice(0, 15))} inputMode="numeric" maxLength={15} placeholder={L("الرقم الضريبي (15 رقم)", "VAT number (15 digits)")} className="flex-1 min-w-40 rounded-lg border border-border px-3 py-1.5 text-sm" />
      <button data-testid="create-reg-btn" onClick={create} className="rounded-lg bg-[hsl(217_63%_15%)] text-white text-sm px-3 py-1.5">{L("تسجيل", "Register")}</button>
    </div>
    <p className="text-[10px] text-muted-foreground mt-1">{L("تحقق من الصيغة فقط (15 رقم) — ليس تأكيداً لصحة التسجيل أو حالته لدى الهيئة (ZATCA).", "Format check only (15 digits) — not a confirmation of registration validity/status with ZATCA.")}</p>
    </div>
  );
}

function GroupCard({ env, group, entities, L }) {
  const s = sapi(env);
  const [members, setMembers] = useState([]);
  const [eid, setEid] = useState("");
  const [eff, setEff] = useState("");
  const load = () => s.get(`/structure/groups/${group.id}/members`).then(({ data }) => setMembers(data)).catch(() => {});
  useEffect(() => { load(); }, [env]); // eslint-disable-line
  const add = () => {
    if (!eid) return;
    s.post(`/structure/groups/${group.id}/members`, { legal_entity_id: eid, member_effective_date: eff }).then(() => { toast.success(L("تمت إضافة العضو", "Member added")); setEid(""); setEff(""); load(); }).catch((e) => toast.error(apiError(e)));
  };
  const exit = (mid) => s.post(`/structure/groups/${group.id}/members/${mid}/exit`, {}).then(() => { toast.success(L("تم تسجيل الخروج", "Exit recorded")); load(); }).catch((e) => toast.error(apiError(e)));
  const name = (id) => (entities.find((e) => e.id === id) || {}).legal_name || id;
  return (
    <div className="rounded-lg border border-border p-3 mb-2" data-testid={`group-${group.id}`}>
      <div className="flex items-center gap-2 text-sm font-medium"><Users2 className="h-4 w-4" />{group.group_name} <span className="text-xs text-muted-foreground">{group.group_vat_number}</span></div>
      <div className="mt-2 space-y-1">
        {members.map((m) => (
          <div key={m.id} className="flex items-center justify-between text-xs rounded bg-secondary/50 px-2 py-1">
            <span>{name(m.legal_entity_id)} · {m.member_effective_date || "—"} → {m.member_exit_date || L("حالي", "active")}</span>
            {!m.member_exit_date && <button data-testid={`exit-member-${m.id}`} onClick={() => exit(m.id)} className="text-[hsl(354_60%_45%)] hover:underline">{L("خروج", "Exit")}</button>}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        <select data-testid={`member-entity-${group.id}`} value={eid} onChange={(e) => setEid(e.target.value)} className="rounded border border-border px-2 py-1 text-xs">
          <option value="">{L("كيان عضو", "Member entity")}</option>
          {entities.map((e) => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
        </select>
        <input data-testid={`member-eff-${group.id}`} value={eff} onChange={(e) => setEff(e.target.value)} placeholder="YYYY-MM-DD" className="rounded border border-border px-2 py-1 text-xs w-32" />
        <button data-testid={`add-member-${group.id}`} onClick={add} className="rounded bg-[hsl(217_63%_15%)] text-white text-xs px-2 py-1">{L("إضافة عضو", "Add member")}</button>
      </div>
    </div>
  );
}

// ---------- Workspace ----------
function Workspace({ env, L }) {
  const s = sapi(env);
  const [clients, setClients] = useState([]);
  const [cid, setCid] = useState("");
  const [engagements, setEngagements] = useState([]);
  const [regs, setRegs] = useState([]);
  const [staff, setStaff] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [statusMeta, setStatusMeta] = useState({ labels: {}, transitions: {} });
  const [canOverride, setCanOverride] = useState(false);
  const [sel, setSel] = useState(null);

  useEffect(() => {
    s.get("/structure/clients").then(({ data }) => setClients(data)).catch(() => {});
    api.get("/period-statuses").then(({ data }) => setStatusMeta(data)).catch(() => {});
    api.get("/rbac/me").then(({ data }) => setCanOverride(!!data.can_override)).catch(() => {});
    api.get("/admin/users").then(({ data }) => setStaff((data || []).filter((u) => !["client_admin", "client_user", "client_approver"].includes(u.role)))).catch(() => {
      s.get("/structure/staff").then(({ data }) => setStaff(data || [])).catch(() => setStaff([]));
    });
  }, [env]); // eslint-disable-line

  const loadEng = (id) => {
    setCid(id); setSel(null);
    s.get(`/structure/engagements?client_id=${id}`).then(({ data }) => setEngagements(data)).catch(() => {});
    s.get(`/structure/registrations?client_id=${id}`).then(({ data }) => setRegs(data)).catch(() => {});
    s.get(`/structure/periods?client_id=${id}`).then(({ data }) => setPeriods(data)).catch(() => {});
  };
  const refreshPeriods = () => s.get(`/structure/periods?client_id=${cid}`).then(({ data }) => setPeriods(data)).catch(() => {});

  const lbl = (st) => { const m = statusMeta.labels?.[st]; return m ? (L(m.ar, m.en)) : st; };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <Card>
        <h3 className="font-semibold text-sm mb-3">{L("اختر عميلاً", "Select client")}</h3>
        <select data-testid="ws-client" value={cid} onChange={(e) => loadEng(e.target.value)} className="w-full rounded-lg border border-border px-3 py-2 text-sm mb-4">
          <option value="">{L("— عميل —", "— client —")}</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {cid && <EngForm env={env} cid={cid} regs={regs} staff={staff} onDone={() => loadEng(cid)} L={L} />}
        <div className="mt-4 space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase">{L("الارتباطات", "Engagements")} ({engagements.length})</h4>
          {engagements.map((e) => <PeriodForm key={e.id} env={env} eng={e} onDone={refreshPeriods} L={L} />)}
        </div>
      </Card>

      <Card className="lg:col-span-2">
        <h3 className="font-semibold text-sm mb-3">{L("الفترات الضريبية", "VAT Periods")} ({periods.length})</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {periods.map((p) => (
            <button key={p.id} data-testid={`period-${p.id}`} onClick={() => s.get(`/structure/periods/${p.id}`).then(({ data }) => setSel(data))}
              className={`text-start rounded-lg border p-3 ${sel?.id === p.id ? "border-[hsl(217_63%_15%)]" : "border-border hover:bg-secondary/50"}`}>
              <div className="text-sm font-medium">{p.label || p.id.slice(0, 6)}</div>
              <div className="mt-1 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-secondary">{lbl(p.status)}</div>
            </button>
          ))}
          {cid && !periods.length && <div className="text-xs text-muted-foreground col-span-2 py-6 text-center">{L("أنشئ ارتباطاً ثم فترة.", "Create an engagement then a period.")}</div>}
        </div>
        {sel && <PeriodDetail env={env} period={sel} statusMeta={statusMeta} lbl={lbl} canOverride={canOverride} onChange={(d) => { setSel(d); refreshPeriods(); }} L={L} />}
      </Card>
    </div>
  );
}

function EngForm({ env, cid, regs, staff, onDone, L }) {
  const s = sapi(env);
  const [scope, setScope] = useState("VAT monthly compliance");
  const [reg, setReg] = useState("");
  const [team, setTeam] = useState({ preparer: "", reviewer: "", tax_manager: "", authorized_filer: "" });
  const slots = [["preparer", L("المُعِد", "Preparer")], ["reviewer", L("المراجع", "Reviewer")], ["tax_manager", L("مدير الضرائب", "Tax Manager")], ["authorized_filer", L("المفوّض بالتقديم", "Filer")]];
  const create = () => {
    s.post("/structure/engagements", { client_id: cid, scope, subject_type: "registration", vat_registration_id: reg, team })
      .then(() => { toast.success(L("تم إنشاء الارتباط", "Engagement created")); onDone(); })
      .catch((e) => toast.error(apiError(e)));
  };
  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid="eng-form">
      <div className="text-xs font-semibold text-muted-foreground uppercase">{L("ارتباط جديد", "New engagement")}</div>
      <input data-testid="eng-scope" value={scope} onChange={(e) => setScope(e.target.value)} className="w-full rounded-lg border border-border px-3 py-1.5 text-sm" />
      <select data-testid="eng-reg" value={reg} onChange={(e) => setReg(e.target.value)} className="w-full rounded-lg border border-border px-2 py-1.5 text-sm">
        <option value="">{L("التسجيل الضريبي", "VAT registration")}</option>
        {regs.map((r) => <option key={r.id} value={r.id}>{r.vat_number || r.id.slice(0, 6)}</option>)}
      </select>
      {slots.map(([k, label]) => (
        <select key={k} data-testid={`eng-${k}`} value={team[k]} onChange={(e) => setTeam({ ...team, [k]: e.target.value })} className="w-full rounded-lg border border-border px-2 py-1.5 text-sm">
          <option value="">{label}</option>
          {staff.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
        </select>
      ))}
      <button data-testid="create-eng-btn" onClick={create} className="w-full rounded-lg bg-[hsl(217_63%_15%)] text-white text-sm py-2">{L("إنشاء الارتباط", "Create engagement")}</button>
    </div>
  );
}

function PeriodForm({ env, eng, onDone, L }) {
  const s = sapi(env);
  const [label, setLabel] = useState("");
  const create = () => {
    if (!label) return;
    s.post("/structure/periods", { engagement_id: eng.id, label }).then(() => { toast.success(L("تم إنشاء الفترة", "Period created")); setLabel(""); onDone(); }).catch((e) => toast.error(apiError(e)));
  };
  return (
    <div className="rounded-lg border border-border p-2 flex items-center gap-2" data-testid={`eng-row-${eng.id}`}>
      <span className="text-xs flex-1 truncate">{eng.scope}</span>
      <input data-testid={`period-label-${eng.id}`} value={label} onChange={(e) => setLabel(e.target.value)} placeholder={L("فترة", "Period")} className="w-24 rounded border border-border px-2 py-1 text-xs" />
      <button data-testid={`create-period-${eng.id}`} onClick={create} className="rounded bg-[hsl(217_63%_15%)] text-white text-xs px-2 py-1">+</button>
    </div>
  );
}

function PeriodDetail({ env, period, statusMeta, lbl, canOverride, onChange, L }) {
  const s = sapi(env);
  const next = statusMeta.transitions?.[period.status] || [];
  const transition = (to) => {
    const reason = window.prompt(L("سبب تغيير الحالة (اختياري):", "Reason (optional):")) || "";
    s.post(`/structure/periods/${period.id}/transition`, { to, reason }).then(({ data }) => { toast.success(L("تم تحديث الحالة", "Status updated")); onChange(data); })
      .catch((e) => {
        const msg = apiError(e);
        if (canOverride && /assigned|override|مُسند|صلاح/i.test(msg)) {
          const r = window.prompt(L("لست المُسنَد لهذه المهمة. أدخل سبب التجاوز المحكوم للمتابعة:", "You are not the assignee. Enter a controlled-override reason to proceed:"), "");
          if (r && r.trim()) {
            s.post(`/structure/periods/${period.id}/transition`, { to, reason: r, override: true }).then(({ data }) => { toast.success(L("تم التجاوز المحكوم وتحديث الحالة", "Controlled override applied")); onChange(data); }).catch((e2) => toast.error(apiError(e2)));
            return;
          }
        }
        toast.error(msg);
      });
  };
  const owners = period.owners || {};
  return (
    <div className="mt-5 pt-5 border-t border-border" data-testid="period-detail">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-[hsl(217_63%_15%)]">{period.label}</h4>
        <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[hsl(217_63%_15%)] text-white" data-testid="period-status">{lbl(period.status)}</span>
      </div>
      <div className="mt-3">
        <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">{L("أصحاب المهام (فصل المهام)", "Owners (SoD)")}</div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          {["preparer", "reviewer", "tax_manager", "authorized_filer"].map((k) => (
            <span key={k} className="px-2 py-1 rounded bg-secondary">{k}: {owners[k] ? owners[k].slice(0, 6) : "—"}</span>
          ))}
        </div>
      </div>
      <div className="mt-4">
        <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("الانتقالات المتاحة", "Available transitions")}</div>
        {next.length ? (
          <div className="flex flex-wrap gap-2">
            {next.map((to) => (
              <button key={to} data-testid={`transition-${to}`} onClick={() => transition(to)}
                className="inline-flex items-center gap-1 rounded-lg border border-[hsl(217_63%_15%)] text-[hsl(217_63%_15%)] text-xs px-3 py-1.5 hover:bg-[hsl(217_63%_15%)] hover:text-white transition-colors">
                <ChevronRight className="h-3.5 w-3.5" />{lbl(to)}
              </button>
            ))}
          </div>
        ) : <div className="flex items-center gap-1 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" />{L("لا انتقالات (حالة نهائية)", "No further transitions")}</div>}
      </div>
      <div className="mt-4">
        <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("سجل الحالات", "Status history")}</div>
        <div className="space-y-1">
          {(period.status_history || []).slice().reverse().map((h, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <CheckCircle2 className="h-3 w-3 text-[hsl(41_72%_50%)]" />{lbl(h.from || "—")} → {lbl(h.to)} · {h.by || "—"} {h.override ? "· ⚠ تجاوز محكوم" : ""} {h.reason ? `· ${h.reason}` : ""}
            </div>
          ))}
        </div>
      </div>
      <DataPanel env={env} period={period} L={L} />
    </div>
  );
}

function DataPanel({ env, period, L }) {
  const s = sapi(env);
  const [requests, setRequests] = useState([]);
  const [lay, setLay] = useState({ raw: 0, standardized: 0, adjusted: 0, imports: [] });
  const [exc, setExc] = useState([]);
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState("sales");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    s.get(`/intake/periods/${period.id}/data-requests`).then(({ data }) => setRequests(data)).catch(() => {});
    s.get(`/intake/periods/${period.id}/layers`).then(({ data }) => setLay(data)).catch(() => {});
    s.get(`/intake/periods/${period.id}/validation-exceptions`).then(({ data }) => setExc(data)).catch(() => {});
  }, [period.id, env]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const seed = () => s.post(`/intake/periods/${period.id}/data-requests/seed`, {}).then(() => { toast.success(L("تم إنشاء طلب البيانات", "Data request created")); load(); }).catch((e) => toast.error(apiError(e)));
  const setItem = (id, status) => api.patch(`/intake/data-request-items/${id}`, { status }, { headers: { "X-Environment": env } }).then(load).catch((e) => toast.error(apiError(e)));
  const upload = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("ledger_type", ledger);
    setBusy(true);
    api.post(`/intake/periods/${period.id}/import`, fd, { headers: { "X-Environment": env } })
      .then(({ data }) => { toast.success(L(`تم الاستيراد: ${data.row_count} صف`, `Imported ${data.row_count} rows`)); load(); })
      .catch((err) => toast.error(apiError(err))).finally(() => { setBusy(false); e.target.value = ""; });
  };
  const validate = () => s.post(`/intake/periods/${period.id}/validate`, {}).then(({ data }) => { setSummary(data); toast.success(L("اكتمل التحقق", "Validation complete")); load(); }).catch((e) => toast.error(apiError(e)));

  const riskColor = (r) => r === "RED" ? "bg-[hsl(354_60%_45%)]" : r === "AMBER" ? "bg-[hsl(41_72%_50%)]" : "bg-[hsl(152_55%_40%)]";

  return (
    <div className="mt-6 pt-5 border-t border-border" data-testid="data-panel">
      <h4 className="font-semibold text-[hsl(217_63%_15%)] mb-3">{L("البيانات والتحقق (السبرنت 2)", "Data & Validation")}</h4>

      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-muted-foreground uppercase">{L("طلب البيانات", "Data Request")} ({requests.length})</span>
        {!requests.length && <button data-testid="seed-requests-btn" onClick={seed} className="text-xs rounded bg-[hsl(217_63%_15%)] text-white px-2 py-1">{L("إنشاء قائمة الطلب", "Create request list")}</button>}
      </div>
      <div className="grid sm:grid-cols-2 gap-1 mb-4 max-h-40 overflow-y-auto">
        {requests.map((it) => (
          <div key={it.id} className="flex items-center justify-between gap-2 text-[11px] rounded border border-border px-2 py-1">
            <span className="truncate">{it.category} · {it.label}</span>
            <select data-testid={`dr-status-${it.id}`} value={it.status} onChange={(e) => setItem(it.id, e.target.value)} className="rounded border border-border text-[10px] px-1 py-0.5">
              {["Requested", "AwaitingClient", "Received", "Incomplete", "Rejected", "NotApplicable"].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select data-testid="ledger-type" value={ledger} onChange={(e) => setLedger(e.target.value)} className="rounded border border-border text-xs px-2 py-1.5">
          <option value="sales">{L("مبيعات", "Sales")}</option>
          <option value="purchase">{L("مشتريات", "Purchases")}</option>
          <option value="gl">GL</option>
          <option value="other">{L("أخرى", "Other")}</option>
        </select>
        <label className="text-xs rounded bg-[hsl(217_63%_15%)] text-white px-3 py-1.5 cursor-pointer" data-testid="upload-ledger-btn">
          {busy ? L("جارٍ الرفع…", "Uploading…") : L("رفع ملف CSV/Excel", "Upload CSV/Excel")}
          <input type="file" accept=".csv,.xlsx,.xls" onChange={upload} className="hidden" disabled={busy} />
        </label>
        <button data-testid="run-validation-btn" onClick={validate} className="text-xs rounded border border-[hsl(217_63%_15%)] text-[hsl(217_63%_15%)] px-3 py-1.5 hover:bg-[hsl(217_63%_15%)] hover:text-white">{L("تشغيل التحقق البنيوي", "Run structural validation")}</button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 text-xs" data-testid="layers-summary">
        <span className="rounded-lg bg-secondary px-3 py-1.5">RAW (غير قابل للتغيير): <b>{lay.raw}</b></span>
        <span className="rounded-lg bg-secondary px-3 py-1.5">STANDARDIZED: <b>{lay.standardized}</b></span>
        <span className="rounded-lg bg-secondary px-3 py-1.5">ADJUSTED: <b>{lay.adjusted}</b></span>
      </div>

      {summary && (
        <div className="flex flex-wrap gap-3 mb-3 text-xs" data-testid="validation-summary">
          <span className="rounded bg-[hsl(152_55%_40%)]/15 text-[hsl(152_55%_30%)] px-3 py-1.5">{L("صفوف سليمة", "Clean rows")}: {summary.green_rows}</span>
          <span className="rounded bg-[hsl(41_72%_50%)]/15 text-[hsl(41_72%_35%)] px-3 py-1.5">AMBER: {summary.amber}</span>
          <span className="rounded bg-[hsl(354_60%_45%)]/15 text-[hsl(354_60%_40%)] px-3 py-1.5">RED: {summary.red}</span>
        </div>
      )}

      {exc.length > 0 && (
        <div data-testid="validation-exceptions">
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("استثناءات التحقق", "Validation Exceptions")} ({exc.length})</div>
          <div className="space-y-1 max-h-56 overflow-y-auto">
            {exc.slice(0, 100).map((x) => (
              <div key={x.id} className="flex items-center gap-2 text-[11px] rounded border border-border px-2 py-1">
                <span className={`inline-block w-2 h-2 rounded-full ${riskColor(x.risk)}`}></span>
                <span className="font-mono text-[hsl(217_63%_15%)]">{x.rule_id.replace(/_/g, " ")}</span>
                <span className="text-muted-foreground truncate flex-1">{x.reason}</span>
                <span className="text-[10px] text-muted-foreground">{x.result}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ---------- RBAC ----------
function Rbac({ L }) {
  const [matrix, setMatrix] = useState({ actions: [], roles: [] });
  useEffect(() => { api.get("/rbac/matrix").then(({ data }) => setMatrix(data)).catch(() => {}); }, []);
  const actions = matrix.actions?.length ? matrix.actions : TENANT_ACTIONS;
  return (
    <Card className="overflow-x-auto" >
      <table className="w-full text-xs" data-testid="rbac-table">
        <thead>
          <tr className="text-muted-foreground border-b border-border">
            <th className="text-start py-2 pe-3 sticky start-0 bg-white">{L("الدور", "Role")}</th>
            {actions.map((a) => <th key={a} className="px-1.5 py-2 font-medium whitespace-nowrap">{a}</th>)}
          </tr>
        </thead>
        <tbody>
          {matrix.roles?.map((r) => (
            <tr key={r.key} className="border-b border-border/60" data-testid={`rbac-row-${r.key}`}>
              <td className="py-2 pe-3 sticky start-0 bg-white"><div className="font-medium">{L(r.label?.ar, r.label?.en)}</div><div className="text-[10px] text-muted-foreground">{r.scope}</div></td>
              {actions.map((a) => (
                <td key={a} className="text-center px-1.5">{r.permissions?.includes(a) ? <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(152_55%_40%)] mx-auto" /> : <span className="text-border">·</span>}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ---------- Audit ----------
function Audit({ env, L }) {
  const s = sapi(env);
  const [logs, setLogs] = useState([]);
  useEffect(() => { s.get("/audit-logs").then(({ data }) => setLogs(data)).catch(() => setLogs([])); }, [env]); // eslint-disable-line
  return (
    <Card>
      <h3 className="font-semibold text-sm mb-3">{L("أحدث أحداث التدقيق", "Recent audit events")} · {env}</h3>
      <div className="space-y-1" data-testid="audit-list">
        {logs.map((e) => (
          <div key={e.id} className="flex flex-wrap items-center gap-x-2 text-[11px] border-b border-border/50 py-1.5">
            <span className="font-mono text-[hsl(217_63%_15%)]">{e.action}</span>
            <span className="text-muted-foreground">{e.object_type}</span>
            {e.new_value != null && <span className="text-muted-foreground truncate">→ {typeof e.new_value === "object" ? JSON.stringify(e.new_value) : String(e.new_value)}</span>}
            <span className="ms-auto text-muted-foreground">{e.actor_name} ({e.actor_role})</span>
          </div>
        ))}
        {!logs.length && <div className="text-xs text-muted-foreground py-6 text-center">{L("لا أحداث بعد", "No events yet")}</div>}
      </div>
    </Card>
  );
}

// ---------- Compliance (Sprint 3) ----------
const SALES_TREAT = [
  ["STANDARD_RATED", "خاضعة بالمعدل القياسي", "Standard Rated"],
  ["ZERO_RATED", "خاضعة بنسبة صفر", "Zero Rated"],
  ["EXEMPT", "معفاة", "Exempt"],
  ["OUT_OF_SCOPE", "خارج النطاق", "Out of Scope"],
  ["VAT_GROUP_INTERNAL", "داخلية ضمن المجموعة", "VAT Group Internal"],
  ["REVIEW_REQUIRED", "تتطلب مراجعة", "Review Required"],
];
const PURCHASE_TREAT = [
  ["FULLY_RECOVERABLE", "قابلة للخصم بالكامل", "Fully Recoverable"],
  ["PARTIALLY_RECOVERABLE", "قابلة للخصم جزئياً", "Partially Recoverable"],
  ["NON_RECOVERABLE", "غير قابلة للخصم", "Non-Recoverable"],
  ["REVERSE_CHARGE", "الاحتساب العكسي", "Reverse Charge"],
  ["DEFERRED_PENDING_EVIDENCE", "مؤجلة بانتظار الإثبات", "Deferred Pending Evidence"],
  ["TECHNICAL_REVIEW_REQUIRED", "تتطلب مراجعة فنية", "Technical Review Required"],
];
const TREAT_LABEL = Object.fromEntries([...SALES_TREAT, ...PURCHASE_TREAT].map(([k, ar, en]) => [k, { ar, en }]));
const CAP = {
  manage_rules: ["platform_admin", "tax_admin"],
  review: ["platform_admin", "tax_manager", "supervisor", "quality_reviewer", "management"],
  edit: ["platform_admin", "tax_admin", "tax_manager", "supervisor", "vat_specialist", "treasury_user"],
  export: ["platform_admin", "tax_admin", "tax_manager", "supervisor", "vat_specialist", "quality_reviewer", "management", "authorized_filer", "treasury_user", "auditor"],
};
function treatColor(t) {
  if (["REVIEW_REQUIRED", "TECHNICAL_REVIEW_REQUIRED", "NON_RECOVERABLE"].includes(t)) return "bg-[hsl(354_60%_45%)]/12 text-[hsl(354_60%_40%)]";
  if (["ZERO_RATED", "EXEMPT", "DEFERRED_PENDING_EVIDENCE", "PARTIALLY_RECOVERABLE"].includes(t)) return "bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]";
  if (["OUT_OF_SCOPE", "VAT_GROUP_INTERNAL"].includes(t)) return "bg-secondary text-foreground/70";
  return "bg-[hsl(217_63%_15%)]/10 text-[hsl(217_63%_15%)]";
}

function Compliance({ env, user, L }) {
  const [sub, setSub] = useState("classify");
  const can = (a) => (CAP[a] || []).includes(user?.role);
  return (
    <div className="space-y-4" data-testid="compliance-page">
      <div className="flex gap-1">
        {[["classify", "تصنيف المعاملات", "Classify"], ["rules", "مكتبة القواعد", "Rule Library"]].map(([k, ar, en]) => (
          <button key={k} data-testid={`comp-sub-${k}`} onClick={() => setSub(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${sub === k ? "bg-[hsl(217_63%_15%)] text-white border-transparent" : "bg-white text-foreground/70 border-border hover:bg-secondary"}`}>{L(ar, en)}</button>
        ))}
      </div>
      {sub === "classify" ? <ClassifyWorkspace env={env} can={can} L={L} /> : <RuleManager env={env} can={can} L={L} />}
    </div>
  );
}

function ClassifyWorkspace({ env, can, L }) {
  const s = sapi(env);
  const [clients, setClients] = useState([]);
  const [cid, setCid] = useState("");
  const [engs, setEngs] = useState([]);
  const [eid, setEid] = useState("");
  const [periods, setPeriods] = useState([]);
  const [pid, setPid] = useState("");
  const [cls, setCls] = useState([]);
  const [ledger, setLedger] = useState("sales");
  const [sales, setSales] = useState(null);
  const [purchase, setPurchase] = useState(null);
  const [exc, setExc] = useState([]);
  const [ov, setOv] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { s.get("/structure/clients").then(({ data }) => setClients(data)).catch(() => {}); }, [env]); // eslint-disable-line
  useEffect(() => { setEid(""); setEngs([]); if (cid) s.get(`/structure/engagements?client_id=${cid}`).then(({ data }) => setEngs(data)).catch(() => {}); }, [cid, env]); // eslint-disable-line
  useEffect(() => { setPid(""); setPeriods([]); if (eid) s.get(`/structure/periods?engagement_id=${eid}`).then(({ data }) => setPeriods(data)).catch(() => {}); }, [eid, env]); // eslint-disable-line

  const loadAll = useCallback(() => {
    if (!pid) { setCls([]); setSales(null); setPurchase(null); setExc([]); return; }
    const q = ledger ? `?ledger_type=${ledger}` : "";
    s.get(`/compliance/periods/${pid}/classifications${q}`).then(({ data }) => setCls(data)).catch(() => {});
    s.get(`/compliance/periods/${pid}/sales-review`).then(({ data }) => setSales(data)).catch(() => {});
    s.get(`/compliance/periods/${pid}/purchase-review`).then(({ data }) => setPurchase(data)).catch(() => {});
    s.get(`/compliance/periods/${pid}/compliance-exceptions${q}`).then(({ data }) => setExc(data)).catch(() => {});
  }, [pid, ledger, env]); // eslint-disable-line
  useEffect(() => { loadAll(); }, [loadAll]);

  const act = (url, okmsg) => { if (!pid) return; setBusy(true); s.post(url, {}).then(() => { toast.success(okmsg); loadAll(); }).catch((e) => toast.error(apiError(e))).finally(() => setBusy(false)); };
  const review = ledger === "purchase" ? purchase : sales;
  const treats = ledger === "purchase" ? PURCHASE_TREAT : SALES_TREAT;
  const excColor = (r) => r === "RED" ? "bg-[hsl(354_60%_45%)]" : r === "AMBER" ? "bg-[hsl(41_72%_50%)]" : "bg-[hsl(152_55%_40%)]";

  return (
    <div className="space-y-4" data-testid="classify-workspace">
      <Card>
        <div className="grid sm:grid-cols-3 gap-2">
          <select data-testid="comp-client" value={cid} onChange={(e) => setCid(e.target.value)} className="rounded-lg border border-border px-2 py-2 text-sm">
            <option value="">{L("اختر عميلاً", "Select client")}</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="comp-engagement" value={eid} onChange={(e) => setEid(e.target.value)} disabled={!cid} className="rounded-lg border border-border px-2 py-2 text-sm disabled:opacity-50">
            <option value="">{L("اختر ارتباطاً", "Select engagement")}</option>
            {engs.map((en) => <option key={en.id} value={en.id}>{en.scope || en.id.slice(0, 6)}</option>)}
          </select>
          <select data-testid="comp-period" value={pid} onChange={(e) => setPid(e.target.value)} disabled={!eid} className="rounded-lg border border-border px-2 py-2 text-sm disabled:opacity-50">
            <option value="">{L("اختر فترة", "Select period")}</option>
            {periods.map((p) => <option key={p.id} value={p.id}>{p.label || p.id.slice(0, 6)} · {p.status}</option>)}
          </select>
        </div>
        {pid && (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {can("edit") && <button data-testid="run-classify-btn" disabled={busy} onClick={() => act(`/compliance/periods/${pid}/classify`, L("اكتمل التصنيف", "Classification complete"))} className="text-xs rounded bg-[hsl(217_63%_15%)] text-white px-3 py-1.5 disabled:opacity-50">{L("تشغيل التصنيف", "Run classification")}</button>}
            {can("edit") && <button data-testid="run-sales-review-btn" disabled={busy} onClick={() => act(`/compliance/periods/${pid}/sales-review`, L("اكتملت مراجعة المبيعات", "Sales review complete"))} className="text-xs rounded border border-[hsl(217_63%_15%)] text-[hsl(217_63%_15%)] px-3 py-1.5 hover:bg-[hsl(217_63%_15%)] hover:text-white">{L("مراجعة المبيعات", "Sales review")}</button>}
            {can("edit") && <button data-testid="run-purchase-review-btn" disabled={busy} onClick={() => act(`/compliance/periods/${pid}/purchase-review`, L("اكتملت مراجعة المشتريات", "Purchase review complete"))} className="text-xs rounded border border-[hsl(217_63%_15%)] text-[hsl(217_63%_15%)] px-3 py-1.5 hover:bg-[hsl(217_63%_15%)] hover:text-white">{L("مراجعة المشتريات", "Purchase review")}</button>}
            {can("review") && <button data-testid="approve-classifications-btn" disabled={busy} onClick={() => act(`/compliance/periods/${pid}/classify-approve`, L("تم اعتماد التصنيفات", "Classifications approved"))} className="text-xs rounded border border-[hsl(152_55%_40%)] text-[hsl(152_55%_30%)] px-3 py-1.5 hover:bg-[hsl(152_55%_40%)] hover:text-white"><CheckCircle2 className="h-3.5 w-3.5 inline me-1" />{L("اعتماد المراجع", "Reviewer approve")}</button>}
          </div>
        )}
      </Card>

      {pid && (
        <>
          <div className="flex items-center gap-2">
            {["sales", "purchase"].map((lt) => (
              <button key={lt} data-testid={`comp-ledger-${lt}`} onClick={() => setLedger(lt)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${ledger === lt ? "bg-[hsl(217_63%_15%)] text-white border-transparent" : "bg-white text-foreground/70 border-border hover:bg-secondary"}`}>
                {lt === "sales" ? L("المبيعات", "Sales") : L("المشتريات", "Purchases")}
              </button>
            ))}
          </div>

          {review?.totals && (
            <Card>
              <div className="flex flex-wrap gap-3 text-xs" data-testid="comp-review-totals">
                <span className="rounded-lg bg-secondary px-3 py-1.5">{L("معاملات", "Txns")}: <b>{review.totals.transactions}</b></span>
                {ledger === "sales" ? (
                  <>
                    <span className="rounded-lg bg-secondary px-3 py-1.5">{L("الوعاء", "Base")}: <b>{review.totals.taxable_base}</b></span>
                    <span className="rounded-lg bg-secondary px-3 py-1.5">{L("ضريبة المخرجات", "Output VAT")}: <b>{review.totals.output_vat}</b></span>
                  </>
                ) : (
                  <>
                    <span className="rounded-lg bg-secondary px-3 py-1.5">{L("قابلة للخصم", "Recoverable")}: <b>{review.totals.recoverable_vat}</b></span>
                    <span className="rounded-lg bg-secondary px-3 py-1.5">{L("احتساب عكسي", "Reverse chg")}: <b>{review.totals.reverse_charge_vat}</b></span>
                    <span className="rounded-lg bg-secondary px-3 py-1.5">{L("غير قابلة", "Non-rec")}: <b>{review.totals.non_recoverable_vat}</b></span>
                  </>
                )}
                <span className="rounded-lg bg-[hsl(152_55%_40%)]/15 text-[hsl(152_55%_30%)] px-3 py-1.5">{L("التغطية", "Coverage")}: {review.totals.coverage_pct}%</span>
                <span className="rounded bg-[hsl(41_72%_50%)]/15 text-[hsl(41_72%_35%)] px-3 py-1.5">AMBER: {review.totals.amber}</span>
                <span className="rounded bg-[hsl(354_60%_45%)]/15 text-[hsl(354_60%_40%)] px-3 py-1.5">RED: {review.totals.red}</span>
              </div>
            </Card>
          )}

          <Card className="overflow-x-auto">
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("طبقات المعالجة الضريبية", "Tax treatment layers")} · {cls.length}</div>
            {!cls.length ? <div className="text-xs text-muted-foreground py-6 text-center">{L("شغّل التصنيف لعرض النتائج.", "Run classification to see results.")}</div> : (
              <table className="w-full text-[11px]" data-testid="classification-table">
                <thead>
                  <tr className="text-muted-foreground border-b border-border">
                    <th className="text-start py-2 pe-2">{L("المستند", "Doc")}</th>
                    <th className="text-start py-2 px-2 bg-secondary/40">{L("المصدر (معلوماتي)", "SOURCE (info)")}</th>
                    <th className="text-start py-2 px-2 bg-[hsl(217_63%_15%)]/5">{L("المتوقّع (القاعدة)", "EXPECTED (rule)")}</th>
                    <th className="text-start py-2 px-2 bg-[hsl(152_55%_40%)]/8">{L("النهائي (مراجعة)", "FINAL (reviewed)")}</th>
                    <th className="py-2 ps-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {cls.slice(0, 200).map((c) => (
                    <tr key={c.id} className="border-b border-border/50 align-top" data-testid={`cls-row-${c.std_id}`}>
                      <td className="py-2 pe-2">
                        <div className="font-medium">{c.document_number || c.std_id.slice(0, 6)}</div>
                        <div className="text-muted-foreground">{L("وعاء", "base")} {c.amount_excl ?? "—"} · {L("ضريبة", "vat")} {c.vat_amount ?? "—"}</div>
                      </td>
                      <td className="py-2 px-2 bg-secondary/40 text-muted-foreground">
                        {L("نسبة", "rate")}: {c.source_vat_rate ?? "—"}<br />{L("رمز", "code")}: {c.source_tax_code || "—"}
                      </td>
                      <td className="py-2 px-2 bg-[hsl(217_63%_15%)]/5">
                        <span className={`inline-block px-1.5 py-0.5 rounded ${treatColor(c.expected_treatment)}`}>{L(TREAT_LABEL[c.expected_treatment]?.ar, TREAT_LABEL[c.expected_treatment]?.en) || c.expected_treatment}</span>
                        <div className="text-muted-foreground mt-1">{L("نسبة", "rate")}: {c.expected_vat_rate ?? "—"}</div>
                        {c.rule_code && <div className="text-[10px] text-muted-foreground mt-0.5" title={c.rule_legal_source}>{c.rule_code} v{c.rule_version} · {c.rule_effective_from}</div>}
                      </td>
                      <td className="py-2 px-2 bg-[hsl(152_55%_40%)]/8">
                        {c.final_treatment ? (
                          <>
                            <span className={`inline-block px-1.5 py-0.5 rounded ${treatColor(c.final_treatment)}`}>{L(TREAT_LABEL[c.final_treatment]?.ar, TREAT_LABEL[c.final_treatment]?.en) || c.final_treatment}</span>
                            <div className="text-[10px] text-muted-foreground mt-1">{c.review_status} · {c.reviewed_by}</div>
                            {c.evidence_ref && <div className="text-[10px] text-muted-foreground">{L("إثبات", "ev")}: {c.evidence_ref}</div>}
                          </>
                        ) : (
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${c.review_status === "pending_review" ? "bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]" : "bg-secondary text-muted-foreground"}`}>{c.review_status === "pending_review" ? L("بانتظار المراجعة", "pending review") : L("تلقائي", "auto")}</span>
                        )}
                      </td>
                      <td className="py-2 ps-2 text-end">
                        {can("review") && (
                          <button data-testid={`override-btn-${c.std_id}`} onClick={() => setOv(c)} className="text-[10px] rounded border border-border px-2 py-1 hover:bg-secondary whitespace-nowrap">{L("تجاوز محكوم", "Override")}</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {exc.length > 0 && (
            <Card>
              <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("استثناءات الامتثال", "Compliance Exceptions")} ({exc.length})</div>
              <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="compliance-exceptions">
                {exc.slice(0, 100).map((x) => (
                  <div key={x.id} className="flex items-center gap-2 text-[11px] rounded border border-border px-2 py-1">
                    <span className={`inline-block w-2 h-2 rounded-full ${excColor(x.risk)}`}></span>
                    <span className="font-mono text-[hsl(217_63%_15%)]">{x.rule_id}</span>
                    <span className="text-muted-foreground truncate flex-1">{x.reason}</span>
                    <span className="text-[10px] text-muted-foreground">{x.result}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {ov && <OverrideModal cls={ov} treats={treats} env={env} onClose={() => setOv(null)} onDone={() => { setOv(null); loadAll(); }} L={L} />}
    </div>
  );
}

function OverrideModal({ cls, treats, env, onClose, onDone, L }) {
  const s = sapi(env);
  const [treatment, setTreatment] = useState(cls.final_treatment || cls.expected_treatment || "");
  const [rate, setRate] = useState(cls.final_vat_rate ?? cls.expected_vat_rate ?? "");
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState(cls.evidence_ref || "");
  const [busy, setBusy] = useState(false);
  const evidenceRequired = !!cls.evidence_required;
  const submit = () => {
    if (!reason.trim()) { toast.error(L("السبب إلزامي", "Reason is mandatory")); return; }
    if (evidenceRequired && evidence.trim().length < 3) { toast.error(L("مرجع الإثبات إلزامي لهذا البند", "A supporting evidence reference is required for this line")); return; }
    setBusy(true);
    s.post(`/compliance/std/${cls.std_id}/classify-override`, {
      final_treatment: treatment, final_vat_rate: rate === "" ? null : Number(rate),
      reason: reason.trim(), evidence_ref: evidence.trim(),
    }).then(() => { toast.success(L("تم تطبيق التجاوز المحكوم", "Controlled override applied")); onDone(); })
      .catch((e) => toast.error(apiError(e))).finally(() => setBusy(false));
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="override-modal">
      <div className="bg-white rounded-xl border border-border w-full max-w-md p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-[hsl(217_63%_15%)]">{L("تجاوز محكوم للمراجع", "Controlled reviewer override")}</h4>
          <button data-testid="override-close" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <div className="rounded-lg bg-secondary/50 p-2.5 text-[11px] text-muted-foreground space-y-0.5">
          <div>{L("المستند", "Doc")}: <b className="text-foreground">{cls.document_number || cls.std_id.slice(0, 8)}</b></div>
          <div>{L("المتوقّع (قاعدة معتمدة — غير قابل للتعديل)", "Expected (approved rule — read-only)")}: <b className="text-foreground">{L(TREAT_LABEL[cls.expected_treatment]?.ar, TREAT_LABEL[cls.expected_treatment]?.en) || cls.expected_treatment}</b> @ {cls.expected_vat_rate ?? "—"}</div>
          {cls.rule_code && <div>{cls.rule_code} v{cls.rule_version} · {cls.rule_legal_source}</div>}
        </div>
        <label className="block">
          <span className="text-[11px] text-muted-foreground">{L("المعالجة النهائية", "Final treatment")}</span>
          <select data-testid="override-treatment" value={treatment} onChange={(e) => setTreatment(e.target.value)} className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm">
            {treats.map(([k, ar, en]) => <option key={k} value={k}>{L(ar, en)}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] text-muted-foreground">{L("النسبة النهائية (اختياري)", "Final rate (optional)")}</span>
          <input data-testid="override-rate" value={rate} onChange={(e) => setRate(e.target.value)} inputMode="decimal" placeholder="0.15" className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <span className="text-[11px] text-muted-foreground">{L("السبب (إلزامي)", "Reason (mandatory)")}</span>
          <textarea data-testid="override-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <span className="text-[11px] text-muted-foreground">{evidenceRequired ? L("مرجع الإثبات (إلزامي)", "Supporting evidence / reference (required)") : L("مرجع الإثبات (اختياري)", "Supporting evidence / reference (optional)")}</span>
          <input data-testid="override-evidence" value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder={L("رقم المستند / الرابط", "Document no. / link")} className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button data-testid="override-cancel" onClick={onClose} className="text-xs rounded-lg border border-border px-3 py-2">{L("إلغاء", "Cancel")}</button>
          <button data-testid="override-submit" disabled={busy || !reason.trim() || (evidenceRequired && evidence.trim().length < 3)} onClick={submit} className="text-xs rounded-lg bg-[hsl(217_63%_15%)] text-white px-4 py-2 disabled:opacity-50">{busy ? L("جارٍ…", "Saving…") : L("تطبيق التجاوز", "Apply override")}</button>
        </div>
      </div>
    </div>
  );
}

function RuleManager({ env, can, L }) {
  const s = sapi(env);
  const [rules, setRules] = useState([]);
  const [scope, setScope] = useState("sales");
  const [showNew, setShowNew] = useState(false);
  const [histFor, setHistFor] = useState(null);
  const [hist, setHist] = useState([]);
  const load = useCallback(() => { s.get("/compliance/rules").then(({ data }) => setRules(data)).catch(() => {}); }, [env]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  const archive = (id) => s.post(`/compliance/rules/${id}/archive`, {}).then(() => { toast.success(L("تمت الأرشفة", "Archived")); load(); }).catch((e) => toast.error(apiError(e)));
  const seed = () => s.post("/compliance/rules/seed", {}).then(() => { toast.success(L("تم تحميل القواعد الافتراضية", "Default rules seeded")); load(); }).catch((e) => toast.error(apiError(e)));
  const showHist = (id) => { setHistFor(id); s.get(`/compliance/rules/${id}/history`).then(({ data }) => setHist(data)).catch(() => setHist([])); };
  const filtered = rules.filter((r) => r.scope === scope);
  const treats = scope === "purchase" ? PURCHASE_TREAT : SALES_TREAT;

  return (
    <div className="space-y-4" data-testid="rule-manager">
      <div className="flex flex-wrap items-center gap-2">
        {["sales", "purchase"].map((sc) => (
          <button key={sc} data-testid={`rule-scope-${sc}`} onClick={() => setScope(sc)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${scope === sc ? "bg-[hsl(217_63%_15%)] text-white border-transparent" : "bg-white text-foreground/70 border-border hover:bg-secondary"}`}>
            {sc === "sales" ? L("مبيعات", "Sales") : L("مشتريات", "Purchases")}
          </button>
        ))}
        <div className="ms-auto flex gap-2">
          {can("manage_rules") && !rules.length && <button data-testid="seed-rules-btn" onClick={seed} className="text-xs rounded border border-border px-3 py-1.5 hover:bg-secondary">{L("تحميل القواعد الافتراضية", "Seed defaults")}</button>}
          {can("manage_rules") && <button data-testid="new-rule-btn" onClick={() => setShowNew(!showNew)} className="text-xs rounded bg-[hsl(217_63%_15%)] text-white px-3 py-1.5"><Plus className="h-3.5 w-3.5 inline me-1" />{L("قاعدة جديدة", "New rule")}</button>}
        </div>
      </div>

      {showNew && can("manage_rules") && <RuleForm env={env} scope={scope} treats={treats} onDone={() => { setShowNew(false); load(); }} L={L} />}

      <Card className="overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="rules-table">
          <thead>
            <tr className="text-muted-foreground border-b border-border">
              <th className="text-start py-2 pe-2">{L("الرمز", "Code")}</th>
              <th className="text-start py-2 px-2">{L("المعالجة", "Treatment")}</th>
              <th className="py-2 px-2">{L("النسبة", "Rate")}</th>
              <th className="text-start py-2 px-2">{L("المصدر النظامي", "Legal source")}</th>
              <th className="py-2 px-2">{L("السريان", "Effective")}</th>
              <th className="py-2 px-2">{L("الإصدار", "Ver")}</th>
              <th className="py-2 px-2">{L("الحالة", "Status")}</th>
              <th className="py-2 ps-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-border/50 align-top" data-testid={`rule-row-${r.code}`}>
                <td className="py-2 pe-2"><div className="font-mono font-medium">{r.code}</div><div className="text-muted-foreground">{L(r.name_ar, r.name_en)}</div></td>
                <td className="py-2 px-2"><span className={`inline-block px-1.5 py-0.5 rounded ${treatColor(r.treatment)}`}>{L(TREAT_LABEL[r.treatment]?.ar, TREAT_LABEL[r.treatment]?.en) || r.treatment}</span></td>
                <td className="py-2 px-2 text-center">{r.vat_rate ?? "—"}</td>
                <td className="py-2 px-2 text-muted-foreground max-w-[220px]">{r.legal_source}</td>
                <td className="py-2 px-2 text-center">{r.effective_from}</td>
                <td className="py-2 px-2 text-center">v{r.version}</td>
                <td className="py-2 px-2 text-center">{r.status === "archived" ? <span className="text-muted-foreground">{L("مؤرشف", "archived")}</span> : <span className="text-[hsl(152_55%_30%)]">{L("فعّال", "active")}</span>}</td>
                <td className="py-2 ps-2 text-end whitespace-nowrap">
                  <button data-testid={`rule-history-${r.code}`} onClick={() => showHist(r.id)} className="text-[10px] rounded border border-border px-2 py-1 hover:bg-secondary me-1"><History className="h-3 w-3 inline" /></button>
                  {can("manage_rules") && <RuleEdit env={env} rule={r} treats={treats} onDone={load} L={L} />}
                  {can("manage_rules") && r.status !== "archived" && <button data-testid={`rule-archive-${r.code}`} onClick={() => archive(r.id)} className="text-[10px] rounded border border-border px-2 py-1 hover:bg-secondary ms-1 text-[hsl(354_60%_45%)]">{L("أرشفة", "Archive")}</button>}
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={8} className="text-xs text-muted-foreground py-6 text-center">{L("لا قواعد. حمّل الافتراضية.", "No rules. Seed defaults.")}</td></tr>}
          </tbody>
        </table>
      </Card>

      {histFor && (
        <Card>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase">{L("سجل إصدارات القاعدة", "Rule version history")}</div>
            <button data-testid="hist-close" onClick={() => setHistFor(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
          </div>
          <div className="space-y-1" data-testid="rule-history-list">
            {hist.length ? hist.map((h) => (
              <div key={h.snapshot_id} className="text-[11px] border-b border-border/50 py-1.5 text-muted-foreground">v{h.version} · {h.treatment} @ {h.vat_rate ?? "—"} · {h.legal_source} · {L("أُرشف", "archived")} {h.archived_at?.slice(0, 10)} ({h.archived_by})</div>
            )) : <div className="text-xs text-muted-foreground py-4 text-center">{L("لا إصدارات سابقة", "No prior versions")}</div>}
          </div>
        </Card>
      )}
    </div>
  );
}

function RuleForm({ env, scope, treats, onDone, L }) {
  const s = sapi(env);
  const [f, setF] = useState({ code: "", name_en: "", name_ar: "", treatment: treats[0][0], vat_rate: "0.15", legal_source: "", effective_from: "2018-01-01", priority: "60", keywords: "", needs_evidence: false, review_required: false });
  const up = (k, v) => setF({ ...f, [k]: v });
  const create = () => {
    const body = {
      code: f.code.trim().toUpperCase(), scope, treatment: f.treatment,
      vat_rate: f.vat_rate === "" ? null : Number(f.vat_rate), name_en: f.name_en, name_ar: f.name_ar,
      legal_source: f.legal_source, effective_from: f.effective_from, priority: Number(f.priority) || 60,
      conditions: f.keywords.trim() ? { keyword_any: f.keywords.split(",").map((x) => x.trim()).filter(Boolean) } : {},
      needs_evidence: f.needs_evidence, review_required: f.review_required,
    };
    s.post("/compliance/rules", body).then(() => { toast.success(L("تم إنشاء القاعدة", "Rule created")); onDone(); }).catch((e) => toast.error(apiError(e)));
  };
  return (
    <Card>
      <div className="grid sm:grid-cols-3 gap-2" data-testid="rule-form">
        <input data-testid="rf-code" value={f.code} onChange={(e) => up("code", e.target.value)} placeholder={L("الرمز", "Code")} className="rounded-lg border border-border px-3 py-2 text-sm" />
        <input data-testid="rf-name-ar" value={f.name_ar} onChange={(e) => up("name_ar", e.target.value)} placeholder={L("الاسم بالعربية", "Arabic name")} className="rounded-lg border border-border px-3 py-2 text-sm" />
        <input data-testid="rf-name-en" value={f.name_en} onChange={(e) => up("name_en", e.target.value)} placeholder={L("الاسم بالإنجليزية", "English name")} className="rounded-lg border border-border px-3 py-2 text-sm" />
        <select data-testid="rf-treatment" value={f.treatment} onChange={(e) => up("treatment", e.target.value)} className="rounded-lg border border-border px-2 py-2 text-sm">
          {treats.map(([k, ar, en]) => <option key={k} value={k}>{L(ar, en)}</option>)}
        </select>
        <input data-testid="rf-rate" value={f.vat_rate} onChange={(e) => up("vat_rate", e.target.value)} placeholder={L("النسبة", "Rate")} className="rounded-lg border border-border px-3 py-2 text-sm" />
        <input data-testid="rf-priority" value={f.priority} onChange={(e) => up("priority", e.target.value)} placeholder={L("الأولوية", "Priority")} className="rounded-lg border border-border px-3 py-2 text-sm" />
        <input data-testid="rf-effective" value={f.effective_from} onChange={(e) => up("effective_from", e.target.value)} placeholder="YYYY-MM-DD" className="rounded-lg border border-border px-3 py-2 text-sm" />
        <input data-testid="rf-legal" value={f.legal_source} onChange={(e) => up("legal_source", e.target.value)} placeholder={L("المصدر النظامي", "Legal source")} className="rounded-lg border border-border px-3 py-2 text-sm sm:col-span-2" />
        <input data-testid="rf-keywords" value={f.keywords} onChange={(e) => up("keywords", e.target.value)} placeholder={L("كلمات مفتاحية (مفصولة بفواصل)", "Keywords (comma-separated)")} className="rounded-lg border border-border px-3 py-2 text-sm sm:col-span-3" />
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" data-testid="rf-evidence" checked={f.needs_evidence} onChange={(e) => up("needs_evidence", e.target.checked)} />{L("يتطلب إثباتاً", "Needs evidence")}</label>
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" data-testid="rf-review" checked={f.review_required} onChange={(e) => up("review_required", e.target.checked)} />{L("يتطلب مراجعة", "Review required")}</label>
        <button data-testid="rf-create" onClick={create} className="rounded-lg bg-[hsl(217_63%_15%)] text-white text-sm py-2">{L("إنشاء", "Create")}</button>
      </div>
    </Card>
  );
}

function RuleEdit({ env, rule, treats, onDone, L }) {
  const s = sapi(env);
  const [open, setOpen] = useState(false);
  const [treatment, setTreatment] = useState(rule.treatment);
  const [rate, setRate] = useState(rule.vat_rate ?? "");
  const [legal, setLegal] = useState(rule.legal_source || "");
  const [priority, setPriority] = useState(String(rule.priority ?? 60));
  const save = () => {
    api.patch(`/compliance/rules/${rule.id}`, {
      treatment, vat_rate: rate === "" ? null : Number(rate), legal_source: legal, priority: Number(priority) || 60,
    }, { headers: { "X-Environment": env } })
      .then(() => { toast.success(L("تم تحديث القاعدة (إصدار جديد)", "Rule updated (new version)")); setOpen(false); onDone(); })
      .catch((e) => toast.error(apiError(e)));
  };
  if (!open) return <button data-testid={`rule-edit-${rule.code}`} onClick={() => setOpen(true)} className="text-[10px] rounded border border-border px-2 py-1 hover:bg-secondary ms-1">{L("تعديل", "Edit")}</button>;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid={`rule-edit-modal-${rule.code}`}>
      <div className="bg-white rounded-xl border border-border w-full max-w-sm p-5 space-y-3">
        <div className="flex items-center justify-between"><h4 className="font-semibold text-[hsl(217_63%_15%)]">{L("تعديل القاعدة", "Edit rule")} {rule.code}</h4><button onClick={() => setOpen(false)}><X className="h-4 w-4" /></button></div>
        <select data-testid="re-treatment" value={treatment} onChange={(e) => setTreatment(e.target.value)} className="w-full rounded-lg border border-border px-2 py-2 text-sm">{treats.map(([k, ar, en]) => <option key={k} value={k}>{L(ar, en)}</option>)}</select>
        <input data-testid="re-rate" value={rate} onChange={(e) => setRate(e.target.value)} placeholder={L("النسبة", "Rate")} className="w-full rounded-lg border border-border px-3 py-2 text-sm" />
        <input data-testid="re-priority" value={priority} onChange={(e) => setPriority(e.target.value)} placeholder={L("الأولوية", "Priority")} className="w-full rounded-lg border border-border px-3 py-2 text-sm" />
        <input data-testid="re-legal" value={legal} onChange={(e) => setLegal(e.target.value)} placeholder={L("المصدر النظامي", "Legal source")} className="w-full rounded-lg border border-border px-3 py-2 text-sm" />
        <div className="flex justify-end gap-2"><button onClick={() => setOpen(false)} className="text-xs rounded-lg border border-border px-3 py-2">{L("إلغاء", "Cancel")}</button><button data-testid="re-save" onClick={save} className="text-xs rounded-lg bg-[hsl(217_63%_15%)] text-white px-4 py-2">{L("حفظ (إصدار جديد)", "Save (new version)")}</button></div>
      </div>
    </div>
  );
}
// ---------- Reconciliation (Sprint 4) ----------
const BRIDGE_LABEL = {
  sales: ["المبيعات", "Sales"], purchase: ["المشتريات", "Purchases"],
  vat_gl: ["الأستاذ العام (VAT GL)", "VAT GL"], customs: ["الجمارك/الاستيراد", "Customs"],
  rcm: ["الاحتساب العكسي", "Reverse charge"],
};
const RECON_STATUSES = ["Matched", "Unmatched", "Timing Difference", "Under Investigation", "Awaiting Client", "Resolved", "Accepted Risk", "Closed"];
function reconStatusColor(s) {
  if (s === "BALANCED" || s === "READY") return "bg-[hsl(152_55%_40%)]/15 text-[hsl(152_55%_30%)]";
  if (s === "BLOCKED") return "bg-[hsl(354_60%_45%)]/15 text-[hsl(354_60%_40%)]";
  if (s === "DIFFERENCES") return "bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]";
  return "bg-secondary text-muted-foreground";
}

function Reconciliation({ env, user, L }) {
  const s = sapi(env);
  const can = (a) => (CAP[a] || []).includes(user?.role);
  const canOverride = ["platform_admin", "tax_admin", "tax_manager", "management"].includes(user?.role);
  const [clients, setClients] = useState([]);
  const [cid, setCid] = useState("");
  const [engs, setEngs] = useState([]);
  const [eid, setEid] = useState("");
  const [periods, setPeriods] = useState([]);
  const [pid, setPid] = useState("");
  const [bridges, setBridges] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [exc, setExc] = useState([]);
  const [sel, setSel] = useState("");
  const [detail, setDetail] = useState(null);
  const [ovExc, setOvExc] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { s.get("/structure/clients").then(({ data }) => setClients(data)).catch(() => {}); }, [env]); // eslint-disable-line
  useEffect(() => { setEid(""); setEngs([]); if (cid) s.get(`/structure/engagements?client_id=${cid}`).then(({ data }) => setEngs(data)).catch(() => {}); }, [cid, env]); // eslint-disable-line
  useEffect(() => { setPid(""); setPeriods([]); if (eid) s.get(`/structure/periods?engagement_id=${eid}`).then(({ data }) => setPeriods(data)).catch(() => {}); }, [eid, env]); // eslint-disable-line

  const loadAll = useCallback(() => {
    if (!pid) { setBridges([]); setReadiness(null); setExc([]); setDetail(null); return; }
    s.get(`/reconciliation/periods/${pid}/bridges`).then(({ data }) => setBridges(data)).catch(() => {});
    s.get(`/reconciliation/periods/${pid}/filing-readiness`).then(({ data }) => setReadiness(data)).catch(() => {});
    s.get(`/reconciliation/periods/${pid}/exceptions`).then(({ data }) => setExc(data)).catch(() => {});
  }, [pid, env]); // eslint-disable-line
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (sel && pid) s.get(`/reconciliation/periods/${pid}/bridges/${sel}/items`).then(({ data }) => setDetail(data)).catch(() => {}); }, [sel, pid, env]); // eslint-disable-line

  const runAll = () => { setBusy(true); s.post(`/reconciliation/periods/${pid}/run-all`, {}).then(() => { toast.success(L("اكتملت كل الجسور", "All bridges run")); loadAll(); }).catch((e) => toast.error(apiError(e))).finally(() => setBusy(false)); };
  const runBridge = (b) => { setBusy(true); s.post(`/reconciliation/periods/${pid}/bridges/${b}/run`, {}).then(() => { toast.success(L("تم تشغيل الجسر", "Bridge run")); loadAll(); if (sel === b) s.get(`/reconciliation/periods/${pid}/bridges/${b}/items`).then(({ data }) => setDetail(data)); }).catch((e) => toast.error(apiError(e))).finally(() => setBusy(false)); };

  return (
    <div className="space-y-4" data-testid="reconciliation-page">
      <Card>
        <div className="grid sm:grid-cols-3 gap-2">
          <select data-testid="recon-client" value={cid} onChange={(e) => setCid(e.target.value)} className="rounded-lg border border-border px-2 py-2 text-sm">
            <option value="">{L("اختر عميلاً", "Select client")}</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="recon-engagement" value={eid} onChange={(e) => setEid(e.target.value)} disabled={!cid} className="rounded-lg border border-border px-2 py-2 text-sm disabled:opacity-50">
            <option value="">{L("اختر ارتباطاً", "Select engagement")}</option>
            {engs.map((en) => <option key={en.id} value={en.id}>{en.scope || en.id.slice(0, 6)}</option>)}
          </select>
          <select data-testid="recon-period" value={pid} onChange={(e) => setPid(e.target.value)} disabled={!eid} className="rounded-lg border border-border px-2 py-2 text-sm disabled:opacity-50">
            <option value="">{L("اختر فترة", "Select period")}</option>
            {periods.map((p) => <option key={p.id} value={p.id}>{p.label || p.id.slice(0, 6)} · {p.status}</option>)}
          </select>
        </div>
        {pid && can("edit") && <button data-testid="recon-run-all-btn" disabled={busy} onClick={runAll} className="mt-3 text-xs rounded bg-[hsl(217_63%_15%)] text-white px-3 py-1.5 disabled:opacity-50">{L("تشغيل كل الجسور", "Run all bridges")}</button>}
      </Card>

      {pid && readiness && (
        <Card className={readiness.ready ? "border-[hsl(152_55%_40%)]" : "border-[hsl(354_60%_45%)]"}>
          <div className="flex items-center gap-3" data-testid="filing-readiness-card">
            {readiness.ready
              ? <CheckCircle2 className="h-6 w-6 text-[hsl(152_55%_40%)]" />
              : <Lock className="h-6 w-6 text-[hsl(354_60%_45%)]" />}
            <div className="flex-1">
              <div className="font-semibold text-[hsl(217_63%_15%)]">{readiness.ready ? L("جاهزية التقديم: جاهزة", "Filing readiness: READY") : L("جاهزية التقديم: محجوبة", "Filing readiness: BLOCKED")}</div>
              {!readiness.ready && <div className="text-[11px] text-muted-foreground mt-1" data-testid="readiness-reasons">
                {readiness.reasons.map((r, i) => (
                  <div key={i}>• {r.type === "bridge_not_run" ? L(`لم يُشغّل جسر ${BRIDGE_LABEL[r.bridge]?.[0]}`, `Bridge not run: ${BRIDGE_LABEL[r.bridge]?.[1]}`) : L(`استثناء أحمر مفتوح (${r.code}) — أثر ضريبي ${r.vat_impact}`, `Open RED (${r.code}) — VAT impact ${r.vat_impact}`)}</div>
                ))}
              </div>}
            </div>
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${reconStatusColor(readiness.status)}`}>{readiness.status}</span>
          </div>
        </Card>
      )}

      {pid && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="bridge-cards">
          {bridges.map((b) => (
            <Card key={b.bridge} className={`cursor-pointer transition ${sel === b.bridge ? "ring-2 ring-[hsl(217_63%_15%)]" : ""}`} data-testid={`bridge-card-${b.bridge}`}>
              <div className="flex items-center justify-between" onClick={() => setSel(b.bridge)}>
                <div className="font-semibold text-sm text-[hsl(217_63%_15%)]">{L(BRIDGE_LABEL[b.bridge]?.[0], BRIDGE_LABEL[b.bridge]?.[1])}</div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${reconStatusColor(b.status)}`}>{b.status}</span>
              </div>
              {b.status !== "NOT_RUN" ? (
                <div className="mt-2 text-[11px] text-muted-foreground space-y-0.5" onClick={() => setSel(b.bridge)}>
                  <div>{b.source_a_label}: <b className="text-foreground">{b.source_a_total}</b></div>
                  <div>{b.source_b_label}: <b className="text-foreground">{b.source_b_total}</b></div>
                  <div>{L("مطابَق/فروق", "Matched/Diff")}: <b className="text-foreground">{b.matched_count}</b> / <b className="text-foreground">{b.unmatched_count}</b></div>
                  <div>{L("الفرق", "Difference")}: <b className="text-foreground">{b.difference}</b> · {L("أثر ضريبي", "VAT")}: <b className="text-foreground">{b.vat_impact}</b></div>
                  <div><span className="text-[hsl(354_60%_40%)]">RED {b.red}</span> · <span className="text-[hsl(41_72%_35%)]">AMBER {b.amber}</span> · <span className="text-[hsl(152_55%_30%)]">GREEN {b.green}</span></div>
                </div>
              ) : <div className="mt-2 text-[11px] text-muted-foreground">{L("لم يُشغّل بعد", "Not run yet")}</div>}
              {can("edit") && <button data-testid={`run-bridge-${b.bridge}`} disabled={busy} onClick={(e) => { e.stopPropagation(); runBridge(b.bridge); }} className="mt-2 text-[10px] rounded border border-border px-2 py-1 hover:bg-secondary">{L("تشغيل", "Run")}</button>}
            </Card>
          ))}
        </div>
      )}

      {sel && detail?.reconciliation && (
        <ReconItems detail={detail} env={env} can={can} bridge={sel} onDone={() => { s.get(`/reconciliation/periods/${pid}/bridges/${sel}/items`).then(({ data }) => setDetail(data)); loadAll(); }} L={L} />
      )}

      {pid && exc.length > 0 && (
        <Card>
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("استثناءات التسوية", "Reconciliation Exceptions")} ({exc.length})</div>
          <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="recon-exceptions">
            {exc.slice(0, 200).map((x) => (
              <div key={x.id} className="flex items-center gap-2 text-[11px] rounded border border-border px-2 py-1.5" data-testid={`recon-exc-${x.id}`}>
                <span className={`inline-block w-2 h-2 rounded-full ${x.risk === "RED" ? "bg-[hsl(354_60%_45%)]" : x.risk === "AMBER" ? "bg-[hsl(41_72%_50%)]" : "bg-[hsl(152_55%_40%)]"}`}></span>
                <span className="font-mono text-[hsl(217_63%_15%)]">{x.code}</span>
                <span className="text-muted-foreground">{L(BRIDGE_LABEL[x.bridge]?.[0], BRIDGE_LABEL[x.bridge]?.[1])}</span>
                <span className="text-muted-foreground truncate flex-1">{x.reason} · {L("أثر", "VAT")} {x.vat_impact}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${x.status === "OVERRIDDEN" ? "bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]" : x.status === "RESOLVED" ? "bg-[hsl(152_55%_40%)]/15 text-[hsl(152_55%_30%)]" : "bg-secondary text-muted-foreground"}`}>{x.status}</span>
                {x.status === "OPEN" && x.risk === "RED" && canOverride && <button data-testid={`override-exc-${x.id}`} onClick={() => setOvExc(x)} className="text-[10px] rounded border border-border px-2 py-0.5 hover:bg-secondary whitespace-nowrap">{L("تجاوز محكوم", "Override")}</button>}
                {x.override && <span className="text-[10px] text-muted-foreground" title={`${x.override.reason} — ${x.override.approver}`}>{L("تم التجاوز", "overridden")} · {x.override.approver}</span>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {ovExc && <ReconOverrideModal exc={ovExc} env={env} onClose={() => setOvExc(null)} onDone={() => { setOvExc(null); loadAll(); }} L={L} />}
    </div>
  );
}

function ReconItems({ detail, env, can, bridge, onDone, L }) {
  const [showAll, setShowAll] = useState(false);
  const rec = detail.reconciliation;
  const items = (detail.items || []).filter((it) => showAll || it.status !== "Matched");
  return (
    <Card className="overflow-x-auto">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-muted-foreground uppercase">{L("بنود التسوية", "Reconciliation Items")} · {L(BRIDGE_LABEL[bridge]?.[0], BRIDGE_LABEL[bridge]?.[1])} · {L("تشغيلة", "run")} #{rec.run_no}</div>
        <label className="text-[11px] flex items-center gap-1"><input type="checkbox" data-testid="recon-show-all" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />{L("عرض المطابَق", "Show matched")}</label>
      </div>
      <table className="w-full text-[11px]" data-testid="recon-items-table">
        <thead><tr className="text-muted-foreground border-b border-border">
          <th className="text-start py-2 pe-2">{L("المستند", "Doc")}</th>
          <th className="py-2 px-2">{L("محاسبي", "Acct")}</th>
          <th className="py-2 px-2">{L("ضريبي", "Tax A")}</th>
          <th className="py-2 px-2">{L("متوقّع", "Tax B")}</th>
          <th className="py-2 px-2">{L("الفرق", "Diff")}</th>
          <th className="py-2 px-2">{L("أثر ضريبي", "VAT")}</th>
          <th className="text-start py-2 px-2">{L("السبب", "Reason")}</th>
          <th className="text-start py-2 px-2">{L("الحالة", "Status")}</th>
          {can("review") && <th className="py-2 ps-2"></th>}
        </tr></thead>
        <tbody>
          {items.slice(0, 500).map((it) => (
            <ReconItemRow key={it.id} it={it} env={env} canReview={can("review")} onDone={onDone} L={L} />
          ))}
          {!items.length && <tr><td colSpan={9} className="text-xs text-muted-foreground py-6 text-center">{L("لا بنود فروق", "No reconciling items")}</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function ReconItemRow({ it, env, canReview, onDone, L }) {
  const [status, setStatus] = useState(it.status);
  const [owner, setOwner] = useState(it.owner || "");
  const [evidence, setEvidence] = useState(it.evidence_ref || "");
  const [decision, setDecision] = useState(it.reviewer_decision || "");
  const [edit, setEdit] = useState(false);
  const matched = it.status === "Matched";
  const save = () => {
    api.patch(`/reconciliation/items/${it.id}`, { status, owner, evidence_ref: evidence, reviewer_decision: decision }, { headers: { "X-Environment": env } })
      .then(() => { toast.success(L("تم تحديث البند", "Item updated")); setEdit(false); onDone(); })
      .catch((e) => toast.error(apiError(e)));
  };
  return (
    <>
      <tr className="border-b border-border/50" data-testid={`recon-item-${it.id}`}>
        <td className="py-1.5 pe-2">{it.document_number || (it.std_id || "").slice(0, 6)}</td>
        <td className="py-1.5 px-2 text-center">{it.accounting_amount}</td>
        <td className="py-1.5 px-2 text-center">{it.tax_amount}</td>
        <td className="py-1.5 px-2 text-center">{it.expected_tax ?? "—"}</td>
        <td className={`py-1.5 px-2 text-center font-medium ${it.difference ? "text-[hsl(354_60%_40%)]" : ""}`}>{it.difference}</td>
        <td className="py-1.5 px-2 text-center">{it.vat_impact}</td>
        <td className="py-1.5 px-2 text-muted-foreground">{it.reason_category || "—"}</td>
        <td className="py-1.5 px-2"><span className={`px-1.5 py-0.5 rounded text-[10px] ${matched ? "bg-[hsl(152_55%_40%)]/15 text-[hsl(152_55%_30%)]" : "bg-secondary text-foreground/70"}`}>{it.status}</span></td>
        {canReview && <td className="py-1.5 ps-2 text-end">{!matched && <button data-testid={`recon-item-edit-${it.id}`} onClick={() => setEdit(!edit)} className="text-[10px] rounded border border-border px-2 py-0.5 hover:bg-secondary">{L("معالجة", "Resolve")}</button>}</td>}
      </tr>
      {edit && canReview && (
        <tr className="bg-secondary/40"><td colSpan={9} className="p-2">
          <div className="grid sm:grid-cols-4 gap-2 items-end">
            <label className="block"><span className="text-[10px] text-muted-foreground">{L("الحالة", "Status")}</span>
              <select data-testid={`recon-status-${it.id}`} value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1 w-full rounded-lg border border-border px-2 py-1.5 text-xs">
                {RECON_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
              </select></label>
            <label className="block"><span className="text-[10px] text-muted-foreground">{L("المسؤول", "Owner")}</span><input data-testid={`recon-owner-${it.id}`} value={owner} onChange={(e) => setOwner(e.target.value)} className="mt-1 w-full rounded-lg border border-border px-2 py-1.5 text-xs" /></label>
            <label className="block"><span className="text-[10px] text-muted-foreground">{L("مرجع الإثبات", "Evidence")}</span><input data-testid={`recon-evidence-${it.id}`} value={evidence} onChange={(e) => setEvidence(e.target.value)} className="mt-1 w-full rounded-lg border border-border px-2 py-1.5 text-xs" /></label>
            <label className="block"><span className="text-[10px] text-muted-foreground">{L("قرار المراجع", "Reviewer decision")}</span><input data-testid={`recon-decision-${it.id}`} value={decision} onChange={(e) => setDecision(e.target.value)} className="mt-1 w-full rounded-lg border border-border px-2 py-1.5 text-xs" /></label>
            <button data-testid={`recon-item-save-${it.id}`} onClick={save} className="text-xs rounded-lg bg-[hsl(217_63%_15%)] text-white px-4 py-1.5 w-max">{L("حفظ", "Save")}</button>
          </div>
        </td></tr>
      )}
    </>
  );
}

function ReconOverrideModal({ exc, env, onClose, onDone, L }) {
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = () => {
    if (!reason.trim()) { toast.error(L("السبب إلزامي", "Reason is mandatory")); return; }
    setBusy(true);
    api.post(`/reconciliation/exceptions/${exc.id}/override`, { reason: reason.trim(), evidence_ref: evidence.trim() }, { headers: { "X-Environment": env } })
      .then(() => { toast.success(L("تم التجاوز المحكوم", "Controlled override applied")); onDone(); })
      .catch((e) => toast.error(apiError(e))).finally(() => setBusy(false));
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="recon-override-modal">
      <div className="bg-white rounded-xl border border-border w-full max-w-md p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-[hsl(217_63%_15%)]">{L("تجاوز محكوم لاستثناء أحمر", "Controlled override — RED exception")}</h4>
          <button data-testid="recon-override-close" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="rounded-lg bg-[hsl(354_60%_45%)]/8 p-2.5 text-[11px] space-y-0.5">
          <div className="flex items-center gap-1 text-[hsl(354_60%_40%)] font-semibold"><AlertTriangle className="h-3.5 w-3.5" />{exc.code} · {L("الخطر الأصلي", "Original risk")}: {exc.original_risk}</div>
          <div className="text-muted-foreground">{exc.reason}</div>
          <div className="text-muted-foreground">{L("الفرق", "Difference")}: {exc.difference} · {L("أثر ضريبي", "VAT impact")}: {exc.vat_impact}</div>
          <div className="text-[10px] text-muted-foreground">{L("الاستثناء الأصلي يُحفَظ ولا يُحذَف.", "The original exception is preserved, never deleted.")}</div>
        </div>
        <label className="block"><span className="text-[11px] text-muted-foreground">{L("السبب (إلزامي)", "Reason (mandatory)")}</span>
          <textarea data-testid="recon-override-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm" /></label>
        <label className="block"><span className="text-[11px] text-muted-foreground">{L("مرجع الإثبات (اختياري)", "Supporting evidence (optional)")}</span>
          <input data-testid="recon-override-evidence" value={evidence} onChange={(e) => setEvidence(e.target.value)} className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm" /></label>
        <div className="flex justify-end gap-2">
          <button data-testid="recon-override-cancel" onClick={onClose} className="text-xs rounded-lg border border-border px-3 py-2">{L("إلغاء", "Cancel")}</button>
          <button data-testid="recon-override-submit" disabled={busy || !reason.trim()} onClick={submit} className="text-xs rounded-lg bg-[hsl(217_63%_15%)] text-white px-4 py-2 disabled:opacity-50">{busy ? L("جارٍ…", "Saving…") : L("تطبيق التجاوز", "Apply override")}</button>
        </div>
      </div>
    </div>
  );
}


// ---------- Working Papers (Sprint 5) ----------
const WP_STATUS_COLOR = {
  APPROVED: "bg-[hsl(152_55%_40%)]/15 text-[hsl(152_55%_30%)]",
  REVIEWED: "bg-[hsl(217_63%_15%)]/10 text-[hsl(217_63%_15%)]",
  IN_REVIEW: "bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]",
  PREPARED: "bg-secondary text-foreground/70",
  REJECTED: "bg-[hsl(354_60%_45%)]/15 text-[hsl(354_60%_40%)]",
  NOT_STARTED: "bg-secondary text-muted-foreground",
};

function WorkPapers({ env, user, L }) {
  const s = sapi(env);
  const can = (a) => (CAP[a] || []).includes(user?.role);
  const canApprove = ["platform_admin", "tax_manager", "management"].includes(user?.role);
  const [clients, setClients] = useState([]);
  const [cid, setCid] = useState("");
  const [engs, setEngs] = useState([]);
  const [eid, setEid] = useState("");
  const [periods, setPeriods] = useState([]);
  const [pid, setPid] = useState("");
  const [wps, setWps] = useState([]);
  const [status, setStatus] = useState(null);
  const [app, setApp] = useState(null);
  const [packages, setPackages] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const lang = L("ar", "en");

  useEffect(() => { s.get("/structure/clients").then(({ data }) => setClients(data)).catch(() => {}); }, [env]); // eslint-disable-line
  useEffect(() => { setEid(""); setEngs([]); if (cid) s.get(`/structure/engagements?client_id=${cid}`).then(({ data }) => setEngs(data)).catch(() => {}); }, [cid, env]); // eslint-disable-line
  useEffect(() => { setPid(""); setPeriods([]); if (eid) s.get(`/structure/periods?engagement_id=${eid}`).then(({ data }) => setPeriods(data)).catch(() => {}); }, [eid, env]); // eslint-disable-line

  const load = useCallback(() => {
    if (!pid) { setWps([]); setStatus(null); setApp(null); setPackages([]); return; }
    s.get(`/wp/periods/${pid}/working-papers`).then(({ data }) => setWps(data)).catch(() => {});
    s.get(`/wp/periods/${pid}/approval-status`).then(({ data }) => setStatus(data)).catch(() => {});
    s.get(`/wp/periods/${pid}/apportionment`).then(({ data }) => setApp(data)).catch(() => {});
    s.get(`/wp/periods/${pid}/packages`).then(({ data }) => setPackages(data)).catch(() => {});
  }, [pid, env]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const act = (p, body, msg) => { setBusy(true); s.post(p, body || {}).then(() => { toast.success(msg); load(); setSel(null); }).catch((e) => toast.error(apiError(e))).finally(() => setBusy(false)); };
  const transition = (wp, action) => act(`/wp/working-papers/${wp.id}/transition`, { action }, L("تم التحديث", "Updated"));

  const downloadPkg = (pkgId) => s.get(`/wp/packages/${pkgId}/download`, { responseType: "blob" }).then((res) => {
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = url; a.download = m ? m[1] : `${pkgId}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  }).catch(() => toast.error(L("تعذّر التنزيل", "Download failed")));

  const exportPkg = () => {
    setBusy(true);
    s.post(`/wp/periods/${pid}/export`, { lang })
      .then(({ data }) => { toast.success(L(`تم إصدار الحزمة ${data.id} (v${data.version})`, `Package ${data.id} (v${data.version}) exported`)); return downloadPkg(data.id); })
      .then(() => load())
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(false));
  };

  const staleCount = status?.stale_count || 0;

  return (
    <div className="space-y-4" data-testid="workpapers-page">
      <Card>
        <div className="grid sm:grid-cols-3 gap-2">
          <select data-testid="wp-client" value={cid} onChange={(e) => setCid(e.target.value)} className="rounded-lg border border-border px-2 py-2 text-sm">
            <option value="">{L("اختر عميلاً", "Select client")}</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="wp-engagement" value={eid} onChange={(e) => setEid(e.target.value)} disabled={!cid} className="rounded-lg border border-border px-2 py-2 text-sm disabled:opacity-50">
            <option value="">{L("اختر ارتباطاً", "Select engagement")}</option>
            {engs.map((en) => <option key={en.id} value={en.id}>{en.scope || en.id.slice(0, 6)}</option>)}
          </select>
          <select data-testid="wp-period" value={pid} onChange={(e) => setPid(e.target.value)} disabled={!eid} className="rounded-lg border border-border px-2 py-2 text-sm disabled:opacity-50">
            <option value="">{L("اختر فترة", "Select period")}</option>
            {periods.map((p) => <option key={p.id} value={p.id}>{p.label || p.id.slice(0, 6)} · {p.status}</option>)}
          </select>
        </div>
        {pid && (can("edit") || can("export")) && <div className="mt-3 flex flex-wrap gap-2">
          {can("edit") && <button data-testid="wp-generate-btn" disabled={busy} onClick={() => act(`/wp/periods/${pid}/generate`, {}, L("تم توليد أوراق العمل", "Working papers generated"))} className="text-xs rounded bg-[hsl(217_63%_15%)] text-white px-3 py-1.5 disabled:opacity-50">{L("توليد أوراق العمل", "Generate working papers")}</button>}
          {can("edit") && <button data-testid="wp-apportion-btn" disabled={busy} onClick={() => act(`/wp/periods/${pid}/apportionment/compute`, {}, L("تم احتساب الخصم الجزئي", "Apportionment computed"))} className="text-xs rounded border border-border px-3 py-1.5 hover:bg-secondary">{L("احتساب الخصم الجزئي", "Compute apportionment")}</button>}
          {can("export") && wps.length > 0 && <button data-testid="wp-export-btn" disabled={busy} onClick={exportPkg} className="text-xs rounded bg-[hsl(152_55%_40%)] text-white px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1.5"><FileStack className="h-3.5 w-3.5" />{L("تصدير حزمة موقّعة (PDF)", "Export signed package (PDF)")}</button>}
        </div>}
      </Card>

      {pid && status && (
        <Card className={status.ready ? "border-[hsl(152_55%_40%)]" : "border-[hsl(354_60%_45%)]"}>
          <div className="flex items-center gap-3" data-testid="wp-approval-status">
            {status.ready ? <CheckCircle2 className="h-6 w-6 text-[hsl(152_55%_40%)]" /> : <Lock className="h-6 w-6 text-[hsl(354_60%_45%)]" />}
            <div className="flex-1">
              <div className="font-semibold text-[hsl(217_63%_15%)]">{status.ready ? L("اعتماد الفترة: جاهزة", "Period approval: READY") : L("اعتماد الفترة: محجوبة", "Period approval: BLOCKED")}</div>
              {!status.ready && <div className="text-[11px] text-muted-foreground mt-1" data-testid="wp-block-reasons">{status.reasons.map((r, i) => <div key={i}>• {r.type} {r.wps ? `(${r.wps.join(", ")})` : r.count ? `(${r.count})` : ""}</div>)}</div>}
            </div>
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${status.ready ? WP_STATUS_COLOR.APPROVED : WP_STATUS_COLOR.REJECTED}`}>{status.status}</span>
          </div>
          {staleCount > 0 && (
            <div className="mt-2 flex items-center gap-2 text-[11px] rounded-lg bg-[hsl(41_72%_50%)]/15 border border-[hsl(41_72%_50%)]/40 text-[hsl(41_72%_30%)] px-3 py-2" data-testid="wp-stale-badge">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-semibold" data-testid="wp-stale-count">{staleCount}</span>
              <span>{L("ورقة عمل معتمدة أصبحت متقادمة وتتطلب إعادة اعتماد بسبب تغيّر البيانات", "approved working paper(s) are STALE and require re-review due to data changes")}</span>
            </div>
          )}
        </Card>
      )}

      {pid && app && (
        <ApportionmentCard app={app} env={env} canApprove={canApprove} onDone={load} L={L} />
      )}

      {pid && wps.length > 0 && (
        <Card>
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("أوراق العمل", "Working Papers")} ({wps.length})</div>
          <div className="space-y-1" data-testid="wp-list">
            {wps.map((w) => (
              <div key={w.id} className="flex items-center gap-2 text-[12px] rounded border border-border px-2 py-2" data-testid={`wp-row-${w.wp_code}`}>
                <span className="font-mono text-[hsl(217_63%_15%)] w-20">{w.wp_code}</span>
                <span className="flex-1">{L(w.title_ar, w.title_en)} {w.mandatory && <span className="text-[9px] text-[hsl(354_60%_45%)]">*</span>}</span>
                {w.stale && <span className="text-[10px] bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)] px-1.5 py-0.5 rounded" title={w.stale_reason}>{L("تتطلب إعادة اعتماد", "STALE")}</span>}
                <span className="text-[9px] text-muted-foreground" title={L("نسخة المصدر", "source version")}>v:{w.source_version}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${WP_STATUS_COLOR[w.status] || ""}`}>{w.status}</span>
                <button data-testid={`wp-open-${w.wp_code}`} onClick={() => setSel(w)} className="text-[10px] rounded border border-border px-2 py-0.5 hover:bg-secondary">{L("فتح", "Open")}</button>
                {can("review") && w.status === "PREPARED" && <button data-testid={`wp-review-${w.wp_code}`} disabled={busy} onClick={() => transition(w, "review")} className="text-[10px] rounded border border-border px-2 py-0.5 hover:bg-secondary">{L("مراجعة", "Review")}</button>}
                {canApprove && w.status === "REVIEWED" && <button data-testid={`wp-approve-${w.wp_code}`} disabled={busy} onClick={() => transition(w, "approve")} className="text-[10px] rounded bg-[hsl(152_55%_40%)] text-white px-2 py-0.5">{L("اعتماد", "Approve")}</button>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {pid && packages.length > 0 && (
        <Card data-testid="wp-packages-card">
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("حِزم أوراق العمل المصدَّرة (أرشيف)", "Exported Packages (Archive)")} ({packages.length})</div>
          <div className="space-y-1" data-testid="wp-packages-list">
            {packages.map((pk) => (
              <div key={pk.id} className="flex items-center gap-2 text-[11px] rounded border border-border px-2 py-2" data-testid={`wp-package-${pk.id}`}>
                <span className="font-mono text-[hsl(217_63%_15%)]">{pk.id}</span>
                <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">v{pk.version}</span>
                <span className="text-[10px] text-muted-foreground">{(pk.generated_at || "").slice(0, 19)}</span>
                <span className="text-[10px] text-muted-foreground">{pk.generated_by} · {pk.generated_by_role}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${pk.period_status === "READY" ? WP_STATUS_COLOR.APPROVED : WP_STATUS_COLOR.REJECTED}`}>{pk.period_status}</span>
                <span className="flex-1 font-mono text-[9px] text-muted-foreground truncate" title={pk.verification_hash}>#{(pk.verification_hash || "").slice(0, 16)}</span>
                <button data-testid={`wp-package-download-${pk.id}`} onClick={() => downloadPkg(pk.id)} className="text-[10px] rounded border border-border px-2 py-0.5 hover:bg-secondary">{L("تنزيل", "Download")}</button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {sel && <WpDetail wp={sel} env={env} can={can} onClose={() => setSel(null)} onNote={load} L={L} />}
    </div>
  );
}

function ApportionmentCard({ app, env, canApprove, onDone, L }) {
  const s = sapi(env);
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState(app.approved_recovery_pct ?? app.calculated_recovery_pct ?? "");
  const [mtype, setMtype] = useState(app.method_type || "standard");
  const [just, setJust] = useState("");
  const [ev, setEv] = useState("");
  const [eff, setEff] = useState("");
  const [basis, setBasis] = useState("");
  const submit = () => {
    if (!just.trim()) { toast.error(L("التبرير إلزامي", "Justification required")); return; }
    s.post(`/wp/periods/${app.period_id}/apportionment/approve`, {
      approved_recovery_pct: pct === "" ? null : Number(pct), method_type: mtype, justification: just.trim(),
      evidence_ref: ev.trim(), effective_date: eff.trim(), regulatory_basis: basis.trim(),
    }).then(() => { toast.success(L("تم اعتماد الخصم الجزئي", "Apportionment approved")); setOpen(false); onDone(); })
      .catch((e) => toast.error(apiError(e)));
  };
  return (
    <Card data-testid="apportionment-card">
      <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("الخصم الجزئي للمدخلات (WP-04)", "Partial Input VAT Deduction")}</div>
      <div className="grid sm:grid-cols-4 gap-2 text-[11px]">
        <div className="rounded bg-secondary px-2 py-1.5">{L("مدخلات متبقّية", "Residual")}: <b>{app.residual_input_vat}</b></div>
        <div className="rounded bg-secondary px-2 py-1.5">{L("مباشرة قابلة للخصم", "Direct recoverable")}: <b>{app.fully_recoverable}</b></div>
        <div className="rounded bg-secondary px-2 py-1.5">{L("النسبة المحسوبة", "Calculated %")}: <b>{app.calculated_recovery_pct}%</b></div>
        <div className={`rounded px-2 py-1.5 ${app.status === "APPROVED" ? "bg-[hsl(152_55%_40%)]/15 text-[hsl(152_55%_30%)]" : "bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]"}`}>{L("النسبة المعتمدة", "Approved %")}: <b>{app.approved_recovery_pct ?? "—"}%</b></div>
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">{L("الطريقة", "Method")}: {app.method_code} ({app.method_type}) · {app.legal_source}</div>
      {canApprove && app.status !== "APPROVED" && <button data-testid="apportion-approve-open" onClick={() => setOpen(!open)} className="mt-2 text-[11px] rounded bg-[hsl(217_63%_15%)] text-white px-3 py-1.5">{L("اعتماد مدير الضرائب", "Tax Manager approval")}</button>}
      {open && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="apportion-approve-form">
          <label className="block"><span className="text-[10px] text-muted-foreground">{L("نوع الطريقة", "Method type")}</span>
            <select data-testid="app-method-type" value={mtype} onChange={(e) => setMtype(e.target.value)} className="mt-1 w-full rounded-lg border border-border px-2 py-1.5 text-xs"><option value="standard">standard</option><option value="alternative">alternative</option></select></label>
          <label className="block"><span className="text-[10px] text-muted-foreground">{L("النسبة المعتمدة %", "Approved %")}</span><input data-testid="app-pct" value={pct} onChange={(e) => setPct(e.target.value)} className="mt-1 w-full rounded-lg border border-border px-2 py-1.5 text-xs" /></label>
          <label className="block sm:col-span-2"><span className="text-[10px] text-muted-foreground">{L("التبرير (إلزامي)", "Justification (mandatory)")}</span><textarea data-testid="app-justification" value={just} onChange={(e) => setJust(e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-border px-2 py-1.5 text-xs" /></label>
          <label className="block"><span className="text-[10px] text-muted-foreground">{L("مرجع الإثبات", "Evidence (alt.)")}</span><input data-testid="app-evidence" value={ev} onChange={(e) => setEv(e.target.value)} className="mt-1 w-full rounded-lg border border-border px-2 py-1.5 text-xs" /></label>
          <label className="block"><span className="text-[10px] text-muted-foreground">{L("تاريخ السريان", "Effective date (alt.)")}</span><input data-testid="app-effective" value={eff} onChange={(e) => setEff(e.target.value)} placeholder="YYYY-MM-DD" className="mt-1 w-full rounded-lg border border-border px-2 py-1.5 text-xs" /></label>
          <label className="block sm:col-span-2"><span className="text-[10px] text-muted-foreground">{L("الأساس النظامي (للطريقة البديلة)", "Regulatory basis (alt.)")}</span><input data-testid="app-basis" value={basis} onChange={(e) => setBasis(e.target.value)} className="mt-1 w-full rounded-lg border border-border px-2 py-1.5 text-xs" /></label>
          <button data-testid="app-approve-submit" onClick={submit} className="text-xs rounded-lg bg-[hsl(152_55%_40%)] text-white px-4 py-1.5 w-max">{L("اعتماد", "Approve")}</button>
        </div>
      )}
    </Card>
  );
}

function WpDetail({ wp, env, can, onClose, onNote, L }) {
  const s = sapi(env);
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [sev, setSev] = useState("Normal");
  const loadNotes = () => s.get(`/wp/periods/${wp.period_id}/notes?wp_code=${wp.wp_code}`).then(({ data }) => setNotes(data)).catch(() => {});
  useEffect(() => { loadNotes(); }, [wp.id]); // eslint-disable-line
  const addNote = () => {
    if (!text.trim()) return;
    s.post(`/wp/periods/${wp.period_id}/notes`, { wp_code: wp.wp_code, text: text.trim(), severity: sev })
      .then(() => { toast.success(L("أُضيفت الملاحظة", "Note added")); setText(""); loadNotes(); onNote && onNote(); }).catch((e) => toast.error(apiError(e)));
  };
  const closeNote = (n) => s.post(`/wp/notes/${n.id}/action`, { action: "close", text: "resolved" }).then(() => { loadNotes(); onNote && onNote(); }).catch((e) => toast.error(apiError(e)));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="wp-detail-modal">
      <div className="bg-white rounded-xl border border-border w-full max-w-2xl p-5 space-y-3 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-[hsl(217_63%_15%)]">{wp.wp_code} · {L(wp.title_ar, wp.title_en)}</h4>
          <button data-testid="wp-detail-close" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="text-[10px] text-muted-foreground">{L("قطع البيانات", "Data cut-off")}: {wp.data_cutoff?.slice(0, 19)} · {L("نسخة المصدر", "source version")}: {wp.source_version} · {L("الحالة", "status")}: {wp.status}{wp.prepared_by ? ` · ${L("أعدّها", "prepared")}: ${wp.prepared_by}` : ""}{wp.approved_by ? ` · ${L("اعتمدها", "approved")}: ${wp.approved_by}` : ""}</div>
        <div className="rounded-lg bg-secondary/40 p-2 text-[11px]">
          <div className="font-semibold mb-1">{L("الإجماليات", "Totals")}</div>
          <pre className="whitespace-pre-wrap text-[10px]">{JSON.stringify(wp.totals, null, 1)}</pre>
        </div>
        <div className="text-[11px]" data-testid="wp-lines">
          <div className="font-semibold mb-1">{L("البنود (مع التتبّع)", "Lines (with traceability)")} ({(wp.lines || []).length})</div>
          <div className="space-y-0.5 max-h-48 overflow-y-auto">
            {(wp.lines || []).slice(0, 100).map((ln, i) => (
              <div key={i} className="border-b border-border/40 py-1 flex gap-2">
                <span className="flex-1">{ln.label}</span>
                {ln.vat != null && <span className="text-muted-foreground">VAT {ln.vat}</span>}
                {ln.risk && <span className="text-[hsl(354_60%_40%)]">{ln.risk}</span>}
                {ln.ok != null && <span className={ln.ok ? "text-[hsl(152_55%_30%)]" : "text-[hsl(354_60%_40%)]"}>{ln.ok ? "✓" : "✗"}</span>}
                {ln.trace?.std_id && <span className="text-[9px] text-muted-foreground" title="trace">↦ {String(ln.trace.std_id).slice(0, 6)}</span>}
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-border pt-2">
          <div className="font-semibold text-[11px] mb-1">{L("ملاحظات المراجعة", "Review Notes")}</div>
          <div className="space-y-1 max-h-40 overflow-y-auto mb-2" data-testid="wp-notes">
            {notes.map((n) => (
              <div key={n.id} className="text-[11px] rounded border border-border px-2 py-1 flex items-center gap-2">
                {n.severity === "Critical" && <AlertTriangle className="h-3 w-3 text-[hsl(354_60%_45%)]" />}
                <span className="flex-1">{n.text}</span>
                <span className="text-[9px] text-muted-foreground">{n.author} · {n.status}</span>
                {can("review") && n.status !== "CLOSED" && <button data-testid={`note-close-${n.id}`} onClick={() => closeNote(n)} className="text-[9px] rounded border border-border px-1.5 py-0.5">{L("إغلاق", "Close")}</button>}
              </div>
            ))}
          </div>
          {can("review") && (
            <div className="flex gap-2" data-testid="wp-add-note">
              <select data-testid="note-severity" value={sev} onChange={(e) => setSev(e.target.value)} className="rounded-lg border border-border px-2 py-1.5 text-xs"><option value="Normal">Normal</option><option value="Critical">Critical</option></select>
              <input data-testid="note-text" value={text} onChange={(e) => setText(e.target.value)} placeholder={L("اكتب ملاحظة…", "Write a note…")} className="flex-1 rounded-lg border border-border px-2 py-1.5 text-xs" />
              <button data-testid="note-add-btn" onClick={addNote} className="text-xs rounded-lg bg-[hsl(217_63%_15%)] text-white px-3 py-1.5">{L("إضافة", "Add")}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------- VAT Returns (Sprint 6) ----------
const RET_STATUS_COLOR = {
  DRAFT: "bg-secondary text-foreground/70",
  IN_REVIEW: "bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]",
  REVIEWED: "bg-[hsl(217_63%_15%)]/10 text-[hsl(217_63%_15%)]",
  AWAITING_CLIENT_APPROVAL: "bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]",
  CLIENT_APPROVED: "bg-[hsl(152_55%_40%)]/15 text-[hsl(152_55%_30%)]",
  READY_TO_FILE: "bg-[hsl(152_55%_40%)]/15 text-[hsl(152_55%_30%)]",
  FILED: "bg-[hsl(217_63%_15%)]/10 text-[hsl(217_63%_15%)]",
  PAYMENT_PENDING: "bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]",
  PAID: "bg-[hsl(152_55%_40%)]/15 text-[hsl(152_55%_30%)]",
  FILED_WITH_CREDIT: "bg-[hsl(152_55%_40%)]/15 text-[hsl(152_55%_30%)]",
  CLOSED: "bg-secondary text-muted-foreground",
};

function Returns({ env, user, L }) {
  const s = sapi(env);
  const can = (a) => (CAP[a] || []).includes(user?.role);
  const canApprove = ["platform_admin", "tax_manager", "management", "tax_admin"].includes(user?.role);
  const canFile = ["platform_admin", "authorized_filer"].includes(user?.role);
  const [clients, setClients] = useState([]); const [cid, setCid] = useState("");
  const [engs, setEngs] = useState([]); const [eid, setEid] = useState("");
  const [periods, setPeriods] = useState([]); const [pid, setPid] = useState("");
  const [schema, setSchema] = useState(null);
  const [ret, setRet] = useState(null); const [versions, setVersions] = useState([]);
  const [ready, setReady] = useState(null); const [busy, setBusy] = useState(false);
  const [zref, setZref] = useState("");
  const [packages, setPackages] = useState([]);
  const lang = L("ar", "en");

  useEffect(() => { s.get("/structure/clients").then(({ data }) => setClients(data)).catch(() => {}); }, [env]); // eslint-disable-line
  useEffect(() => { s.get("/returns/schema").then(({ data }) => setSchema(data)).catch(() => {}); }, [env]); // eslint-disable-line
  useEffect(() => { setEid(""); setEngs([]); if (cid) s.get(`/structure/engagements?client_id=${cid}`).then(({ data }) => setEngs(data)).catch(() => {}); }, [cid, env]); // eslint-disable-line
  useEffect(() => { setPid(""); setPeriods([]); if (eid) s.get(`/structure/periods?engagement_id=${eid}`).then(({ data }) => setPeriods(data)).catch(() => {}); }, [eid, env]); // eslint-disable-line

  const load = useCallback(() => {
    if (!pid) { setRet(null); setVersions([]); setReady(null); setPackages([]); return; }
    s.get(`/returns/periods/${pid}/current`).then(({ data }) => setRet(data)).catch(() => {});
    s.get(`/returns/periods/${pid}/returns`).then(({ data }) => setVersions(data)).catch(() => {});
    s.get(`/returns/periods/${pid}/readiness`).then(({ data }) => setReady(data)).catch(() => {});
    s.get(`/returns/periods/${pid}/packages`).then(({ data }) => setPackages(data)).catch(() => {});
  }, [pid, env]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const act = (p, body, msg) => { setBusy(true); s.post(p, body || {}).then(() => { toast.success(msg); load(); }).catch((e) => toast.error(apiError(e))).finally(() => setBusy(false)); };
  const tr = (action, body) => act(`/returns/${ret.id}/transition`, { action, ...(body || {}) }, L("تم التحديث", "Updated"));

  const downloadPkg = (id) => s.get(`/returns/packages/${id}/download`, { responseType: "blob" }).then((res) => {
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = url; a.download = m ? m[1] : `${id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  }).catch(() => toast.error(L("تعذّر التنزيل", "Download failed")));

  const exportPkg = () => { setBusy(true);
    s.post(`/returns/${ret.id}/export`, { lang })
      .then(({ data }) => { toast.success(L(`تم إصدار الحزمة ${data.id} (v${data.version})`, `Package ${data.id} (v${data.version}) exported`)); return downloadPkg(data.id); })
      .then(() => load()).catch((e) => toast.error(apiError(e))).finally(() => setBusy(false));
  };

  return (
    <div className="space-y-4" data-testid="returns-page">
      <Card>
        <div className="grid sm:grid-cols-3 gap-2">
          <select data-testid="ret-client" value={cid} onChange={(e) => setCid(e.target.value)} className="rounded-lg border border-border px-2 py-2 text-sm">
            <option value="">{L("اختر عميلاً", "Select client")}</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="ret-engagement" value={eid} onChange={(e) => setEid(e.target.value)} disabled={!cid} className="rounded-lg border border-border px-2 py-2 text-sm disabled:opacity-50">
            <option value="">{L("اختر ارتباطاً", "Select engagement")}</option>
            {engs.map((en) => <option key={en.id} value={en.id}>{en.scope || en.id.slice(0, 6)}</option>)}
          </select>
          <select data-testid="ret-period" value={pid} onChange={(e) => setPid(e.target.value)} disabled={!eid} className="rounded-lg border border-border px-2 py-2 text-sm disabled:opacity-50">
            <option value="">{L("اختر فترة", "Select period")}</option>
            {periods.map((p) => <option key={p.id} value={p.id}>{p.label || p.id.slice(0, 6)} · {p.status}</option>)}
          </select>
        </div>
        {schema && <div className="mt-2 text-[10px] text-muted-foreground" data-testid="ret-schema-src">{L("مخطط الإقرار", "Return schema")}: {schema.schema_code} {schema.schema_version} · {schema.official_source}</div>}
        {pid && can("edit") && <div className="mt-3 flex flex-wrap gap-2">
          <button data-testid="ret-generate-btn" disabled={busy} onClick={() => act(`/returns/periods/${pid}/generate`, {}, L("تم توليد مسودة الإقرار", "Draft return generated"))} className="text-xs rounded bg-[hsl(217_63%_15%)] text-white px-3 py-1.5 disabled:opacity-50">{L("توليد مسودة الإقرار (من WP‑10)", "Generate draft (from WP‑10)")}</button>
        </div>}
      </Card>

      {pid && ready && (
        <Card className={ready.ready ? "border-[hsl(152_55%_40%)]" : "border-[hsl(354_60%_45%)]"}>
          <div className="flex items-center gap-3" data-testid="ret-readiness">
            {ready.ready ? <CheckCircle2 className="h-6 w-6 text-[hsl(152_55%_40%)]" /> : <Lock className="h-6 w-6 text-[hsl(354_60%_45%)]" />}
            <div className="flex-1">
              <div className="font-semibold text-[hsl(217_63%_15%)]">{ready.ready ? L("بوّابة الجاهزية: جاهز للتقديم", "Filing readiness: READY") : L("بوّابة الجاهزية: محجوبة", "Filing readiness: BLOCKED")}</div>
              {!ready.ready && <div className="text-[11px] text-muted-foreground mt-1" data-testid="ret-ready-reasons">{(ready.reasons || []).map((r, i) => <div key={i}>• {r.type}{r.status ? ` (${r.status})` : ""}</div>)}</div>}
            </div>
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${ready.ready ? RET_STATUS_COLOR.READY_TO_FILE : RET_STATUS_COLOR.DRAFT}`}>{ready.status}</span>
          </div>
        </Card>
      )}

      {pid && ret && (
        <Card data-testid="ret-current">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase">{L("الإقرار الحالي", "Current Return")} · {ret.id} · v{ret.version}</div>
            <div className="flex items-center gap-2">
              {ret.stale && <span className="text-[10px] bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)] px-1.5 py-0.5 rounded" data-testid="ret-stale">{L("متقادم — إعادة توليد", "STALE — RE-GENERATE")}</span>}
              <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${RET_STATUS_COLOR[ret.status] || ""}`} data-testid="ret-status">{ret.status}</span>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-2 text-[11px] mb-3">
            <div className="rounded bg-secondary px-2 py-1.5">{L("صافي الضريبة (داخلي)", "Net VAT (internal)")}: <b>{ret.net_internal}</b></div>
            <div className="rounded bg-secondary px-2 py-1.5">{L("المركز", "Position")}: <b>{ret.net_position}</b></div>
            <div className="rounded bg-secondary px-2 py-1.5">{L("WP‑10 ver", "WP‑10 ver")}: <b>{ret.wp10_version}</b> · src:{ret.source_version}</div>
          </div>
          <div className="overflow-x-auto" data-testid="ret-boxes">
            <table className="w-full text-[11px]">
              <thead><tr className="text-muted-foreground border-b border-border"><th className="text-right py-1">#</th><th className="text-right">{L("الخانة", "Box")}</th><th className="text-left">{L("المبلغ", "Amount")}</th><th className="text-left">{L("الضريبة", "VAT")}</th><th className="text-left">{L("العرض", "Display")}</th></tr></thead>
              <tbody>
                {(ret.boxes || []).map((b) => (
                  <tr key={b.box} className="border-b border-border/40" data-testid={`ret-box-${b.box}`}>
                    <td className="py-1 font-mono text-[hsl(217_63%_15%)]">{b.box}</td>
                    <td>{L(b.label_ar, b.label_en)}{b.calc_effect === "rcm_net" && b.trace ? <span className="text-[9px] text-muted-foreground block">RCM out {b.trace.rcm_output_vat} · elig {b.trace.rcm_eligible_input_vat} · nonded {b.trace.rcm_non_deductible_input_vat} · net {b.trace.rcm_net_vat_effect}</span> : null}</td>
                    <td className="text-left tabular-nums">{b.amount == null ? "—" : b.amount}</td>
                    <td className="text-left tabular-nums">{b.has_vat ? b.vat : "—"}</td>
                    <td className="text-left tabular-nums text-muted-foreground">{b.display_value == null ? "—" : b.display_value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="ret-actions">
            {ret.status === "DRAFT" && can("edit") && <button data-testid="ret-submit" disabled={busy || ret.stale} onClick={() => tr("submit")} className="text-[11px] rounded border border-border px-3 py-1.5 hover:bg-secondary">{L("إرسال للمراجعة", "Submit for review")}</button>}
            {ret.status === "IN_REVIEW" && can("review") && <button data-testid="ret-review" disabled={busy} onClick={() => tr("review")} className="text-[11px] rounded border border-border px-3 py-1.5 hover:bg-secondary">{L("مراجعة", "Review")}</button>}
            {ret.status === "REVIEWED" && can("review") && <button data-testid="ret-send-client" disabled={busy} onClick={() => tr("send_to_client")} className="text-[11px] rounded border border-border px-3 py-1.5 hover:bg-secondary">{L("إرسال للعميل", "Send to client")}</button>}
            {ret.status === "AWAITING_CLIENT_APPROVAL" && canApprove && <button data-testid="ret-client-approve" disabled={busy} onClick={() => tr("client_approve")} className="text-[11px] rounded bg-[hsl(152_55%_40%)] text-white px-3 py-1.5">{L("اعتماد (نيابة عن العميل)", "Approve (on behalf)")}</button>}
            {ret.status === "CLIENT_APPROVED" && canApprove && <button data-testid="ret-mark-ready" disabled={busy} onClick={() => tr("mark_ready")} className="text-[11px] rounded bg-[hsl(217_63%_15%)] text-white px-3 py-1.5">{L("تحديد جاهز للتقديم", "Mark ready to file")}</button>}
            {ret.status === "READY_TO_FILE" && canFile && <span className="flex items-center gap-2"><input data-testid="ret-zatca-ref" value={zref} onChange={(e) => setZref(e.target.value)} placeholder={L("مرجع تقديم ZATCA", "ZATCA ref")} className="rounded-lg border border-border px-2 py-1.5 text-[11px]" /><button data-testid="ret-file" disabled={busy || !zref.trim()} onClick={() => tr("file", { zatca_reference: zref.trim() })} className="text-[11px] rounded bg-[hsl(152_55%_40%)] text-white px-3 py-1.5 disabled:opacity-50">{L("تقديم (مفوَّض)", "File (authorized)")}</button></span>}
            {ret.status === "PAYMENT_PENDING" && <button data-testid="ret-pay" disabled={busy} onClick={() => tr("record_payment", { payment_status: "PAID" })} className="text-[11px] rounded bg-[hsl(152_55%_40%)] text-white px-3 py-1.5">{L("تسجيل سداد (SADAD)", "Record payment")}</button>}
            {["FILED", "PAID", "FILED_WITH_CREDIT"].includes(ret.status) && canApprove && <button data-testid="ret-close" disabled={busy} onClick={() => tr("close")} className="text-[11px] rounded border border-border px-3 py-1.5 hover:bg-secondary">{L("إغلاق", "Close")}</button>}
            {can("export") && <button data-testid="ret-export-btn" disabled={busy} onClick={exportPkg} className="text-[11px] rounded bg-[hsl(152_55%_40%)] text-white px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1.5"><FileStack className="h-3.5 w-3.5" />{L("تصدير حزمة موقّعة (PDF)", "Export signed package (PDF)")}</button>}
            {ret.sadad_bill_no && <span className="text-[10px] text-muted-foreground self-center">SADAD: {ret.sadad_bill_no}</span>}
            {ret.zatca_reference && <span className="text-[10px] text-muted-foreground self-center">ZATCA: {ret.zatca_reference}</span>}
          </div>
        </Card>
      )}

      {pid && versions.length > 0 && (
        <Card data-testid="ret-versions">
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("إصدارات الإقرار (أرشيف)", "Return Versions (Archive)")} ({versions.length})</div>
          <div className="space-y-1">
            {versions.map((v) => (
              <div key={v.id} className="flex items-center gap-2 text-[11px] rounded border border-border px-2 py-1.5" data-testid={`ret-version-${v.version}`}>
                <span className="font-mono text-[hsl(217_63%_15%)]">{v.id}</span>
                <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">v{v.version}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${RET_STATUS_COLOR[v.status] || ""}`}>{v.status}</span>
                <span className="text-[10px] text-muted-foreground">net {v.net_internal} · {v.net_position}</span>
                {v.is_current && <span className="text-[9px] text-[hsl(152_55%_30%)]">{L("الحالي", "current")}</span>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {pid && packages.length > 0 && (
        <Card data-testid="ret-packages">
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("حِزم الإقرار الموقّعة (أرشيف)", "Signed Return Packages (Archive)")} ({packages.length})</div>
          <div className="space-y-1">
            {packages.map((pk) => (
              <div key={pk.id} className="flex items-center gap-2 text-[11px] rounded border border-border px-2 py-1.5" data-testid={`ret-package-${pk.id}`}>
                <span className="font-mono text-[hsl(217_63%_15%)]">{pk.id}</span>
                <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">v{pk.version}</span>
                <span className="text-[10px] text-muted-foreground">{(pk.generated_at || "").slice(0, 19)}</span>
                <span className="text-[10px] text-muted-foreground">{pk.generated_by} · {pk.generated_by_role}</span>
                <span className="text-[10px]">{pk.return_status} · net {pk.net_internal}</span>
                <span className="flex-1 font-mono text-[9px] text-muted-foreground truncate" title={pk.verification_hash}>#{(pk.verification_hash || "").slice(0, 16)}</span>
                <button data-testid={`ret-package-download-${pk.id}`} onClick={() => downloadPkg(pk.id)} className="text-[10px] rounded border border-border px-2 py-0.5 hover:bg-secondary">{L("تنزيل", "Download")}</button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
